// CustomFieldsView.jsx — View PURA de /custom-fields (Perguntas do agendamento). View/ViewModel à risca.
// Recebe { vm, actions } — sem fetch, sem validação, sem regra de negócio, sem formatar texto.
// Contrato: customFields.vm.js (@version 1). Quatro superfícies com a MESMA gramática visual:
//   · default CustomFieldsView — a tela (lista ordenável, dados básicos, sugestões, criação, renomear, apagar);
//   · BookingQuestions        — bloco no formulário de novo agendamento (some sem perguntas; erro não trava);
//   · AppointmentAnswers      — bloco no detalhe do agendamento (ver, completar, editar, divergência);
//   · PersonCustomFields      — bloco na ficha da pessoa (valores e divergência como "pequena decisão").
// Com vm.surface != 'screen' a default desenha só o bloco de vm.block (é para o estúdio; no Admin é 'screen').
//
// Ideia da tela — "o formulário tomando forma":
//   · As perguntas são uma coluna numerada, ligada por uma linha tracejada: a ordem do formulário é a ordem
//     na tela. Arrastar pela alça reordena ao vivo; sem arrastar, ↑/↓ no cartão (toque) e as setas do teclado
//     na alça fazem o mesmo, com o anúncio da nova posição para o leitor de tela.
//   · "Aparece em" (audienceLabel) vira uma trilha de pastilhas — serviço · profissional · local —: o recorte
//     específico ganha a cor da marca, o "todos" fica quieto e o "nenhum" fica no tom de aviso. A frase do vm
//     continua inteira para o leitor de tela (a View só a desenha em pedaços; não escreve nada novo).
//   · Pergunta que não aparece para ninguém (warning 'emptyFilter') pinta o cartão no tom de aviso, e o resumo
//     no topo vira um atalho que leva o foco até ela.
//   · Dados básicos são "ligar um recurso": chave Usar/Não usar, o ícone acende ao ligar — nada de criar.
//   · Vazio (a tela mais comum): um formulário-esboço com os exemplos, que se tocam, e as perguntas prontas
//     do nicho em destaque.
//   · Criação: janela com os passos à esquerda e a PRÉVIA AO VIVO à direita (no celular, tela cheia, a prévia
//     entra logo depois do tipo). A frase de "aparece em" pulsa a cada mudança. O aviso de que a pergunta não
//     se edita fica colado no botão de criar, em tom calmo.
//
// Estado só de UI: arraste (posições enquanto o dedo está na tela), aba do grupo de tipos, rascunho da nova
// opção, valor brincado na prévia, modo "editar respostas", e foco/rolagem da confirmação de apagar (mesma
// regra de Gestão de Acessos: foco sem rolar + scrollIntoView 'nearest'; no primeiro desenho nada se mexe).
// Consistência: mesmo CARD_SX, hover, botões, pastilhas, Hint e StateBlock de Serviços e Gestão de Acessos.
// Regras: estilo só via `sx`; cores/medidas via var(--*); MUI v6 + @mui/icons-material; tipografia via variant.
import { Fragment, useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Button, IconButton, Tooltip, TextField, MenuItem, Switch, Dialog, Skeleton,
  CircularProgress, ButtonBase, Checkbox, InputAdornment, ToggleButton, ToggleButtonGroup,
  useMediaQuery, keyframes,
} from '@mui/material';
import DynamicFormRounded from '@mui/icons-material/DynamicFormRounded';
import QuizRounded from '@mui/icons-material/QuizRounded';
import AddRounded from '@mui/icons-material/AddRounded';
import LockOutlined from '@mui/icons-material/LockOutlined';
import LockClockRounded from '@mui/icons-material/LockClockRounded';
import DragIndicatorRounded from '@mui/icons-material/DragIndicatorRounded';
import ArrowUpwardRounded from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRounded from '@mui/icons-material/ArrowDownwardRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import ErrorRounded from '@mui/icons-material/ErrorRounded';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import ReplayRounded from '@mui/icons-material/ReplayRounded';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import DesignServicesRounded from '@mui/icons-material/DesignServicesRounded';
import PersonRounded from '@mui/icons-material/PersonRounded';
import PlaceRounded from '@mui/icons-material/PlaceRounded';
import CakeRounded from '@mui/icons-material/CakeRounded';
import BadgeRounded from '@mui/icons-material/BadgeRounded';
import WcRounded from '@mui/icons-material/WcRounded';
import HomeRounded from '@mui/icons-material/HomeRounded';
import ShortTextRounded from '@mui/icons-material/ShortTextRounded';
import NotesRounded from '@mui/icons-material/NotesRounded';
import PinRounded from '@mui/icons-material/PinRounded';
import PaymentsRounded from '@mui/icons-material/PaymentsRounded';
import CalendarTodayRounded from '@mui/icons-material/CalendarTodayRounded';
import ScheduleRounded from '@mui/icons-material/ScheduleRounded';
import RadioButtonCheckedRounded from '@mui/icons-material/RadioButtonCheckedRounded';
import RadioButtonUncheckedRounded from '@mui/icons-material/RadioButtonUncheckedRounded';
import CheckBoxRounded from '@mui/icons-material/CheckBoxRounded';
import CheckBoxOutlineBlankRounded from '@mui/icons-material/CheckBoxOutlineBlankRounded';
import ChecklistRounded from '@mui/icons-material/ChecklistRounded';
import ThumbsUpDownRounded from '@mui/icons-material/ThumbsUpDownRounded';
import TaskAltRounded from '@mui/icons-material/TaskAltRounded';
import PhoneRounded from '@mui/icons-material/PhoneRounded';
import AlternateEmailRounded from '@mui/icons-material/AlternateEmailRounded';
import BusinessRounded from '@mui/icons-material/BusinessRounded';
import MarkunreadMailboxRounded from '@mui/icons-material/MarkunreadMailboxRounded';
import HelpOutlineRounded from '@mui/icons-material/HelpOutlineRounded';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import TouchAppRounded from '@mui/icons-material/TouchAppRounded';
import VisibilityRounded from '@mui/icons-material/VisibilityRounded';
import FolderSharedRounded from '@mui/icons-material/FolderSharedRounded';
import EventNoteRounded from '@mui/icons-material/EventNoteRounded';
import CompareArrowsRounded from '@mui/icons-material/CompareArrowsRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import PauseCircleOutlineRounded from '@mui/icons-material/PauseCircleOutlineRounded';
import KeyboardArrowRightRounded from '@mui/icons-material/KeyboardArrowRightRounded';
import TuneRounded from '@mui/icons-material/TuneRounded';
import ToggleOnRounded from '@mui/icons-material/ToggleOnRounded';

// TÍTULO DE PÁGINA: <Typography variant="h1"> puro — a escala vive no tema pinado.

/* ── Linguagem visual compartilhada (espelho de Serviços / Gestão de Acessos) ── */
const CARD_SX = {
  bgcolor: 'var(--surface)',
  border: '1px solid',
  borderColor: 'var(--divider)',
  borderRadius: { xs: 'var(--radius-card-mobile)', sm: 'var(--radius-card)' },
  boxShadow: 'var(--shadow-card)',
};
const FOCUS_SX = {
  '&.Mui-focusVisible, &:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: '2px' },
};
const ICON_BTN_SX = {
  ...FOCUS_SX,
  width: 40, height: 40, color: 'var(--text-secondary)',
  '&:hover': { color: 'var(--color-primary-dark)', bgcolor: 'var(--color-primary-soft)' },
  '&.Mui-disabled': { color: 'var(--text-disabled)' },
};
const REMOVE_BTN_SX = {
  ...FOCUS_SX,
  width: 40, height: 40, color: 'var(--text-secondary)',
  '&:hover': { color: 'var(--color-error)', bgcolor: 'var(--tint-error-bg)' },
};
const PRIMARY_BTN_SX = {
  ...FOCUS_SX,
  bgcolor: 'var(--color-primary)', color: 'var(--on-primary)',
  borderRadius: 'var(--radius-sm)', px: 2.25, minHeight: 42,
  fontWeight: 'var(--fw-bold)', textTransform: 'none', whiteSpace: 'nowrap',
  '&:hover': { bgcolor: 'var(--color-primary-dark)' },
  '&.Mui-disabled': { bgcolor: 'var(--divider)', color: 'var(--text-disabled)' },
};
const OUTLINE_BTN_SX = {
  ...FOCUS_SX,
  minHeight: 42, px: 2.25, borderRadius: 'var(--radius-sm)',
  border: '1px solid', borderColor: 'var(--color-primary)', color: 'var(--color-primary-dark)',
  fontWeight: 'var(--fw-bold)', textTransform: 'none', whiteSpace: 'nowrap',
  '&:hover': { bgcolor: 'var(--color-primary-soft)' },
  '&.Mui-disabled': { borderColor: 'var(--divider)', color: 'var(--text-disabled)' },
};
const QUIET_BTN_SX = {
  ...FOCUS_SX,
  minHeight: 42, px: 2, borderRadius: 'var(--radius-sm)',
  border: '1px solid', borderColor: 'var(--divider)', color: 'var(--text-secondary)',
  fontWeight: 'var(--fw-medium)', textTransform: 'none', whiteSpace: 'nowrap',
  '&:hover': { borderColor: 'var(--color-primary)', color: 'var(--color-primary-dark)', bgcolor: 'var(--color-primary-soft)' },
  '&.Mui-disabled': { borderColor: 'var(--divider)', color: 'var(--text-disabled)' },
};
// Destrutivo "sem susto" (o mesmo de Gestão de Acessos): pastilha de tint com texto forte.
const DANGER_BTN_SX = {
  ...FOCUS_SX,
  minHeight: 42, px: 2, borderRadius: 'var(--radius-sm)',
  bgcolor: 'var(--surface)', border: '1px solid', borderColor: 'var(--tint-error-fg)', color: 'var(--tint-error-fg)',
  fontWeight: 'var(--fw-bold)', textTransform: 'none', whiteSpace: 'nowrap',
  '&:hover': { bgcolor: 'var(--tint-error-border)' },
  '&.Mui-disabled': { borderColor: 'var(--tint-error-border)', color: 'var(--tint-error-sub)' },
};
const FIELD_SX = {
  '& .MuiOutlinedInput-root': { borderRadius: 'var(--radius-sm)', bgcolor: 'var(--surface)', minHeight: 42 },
  '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--color-error)' },
};
const SWITCH_SX = {
  flexShrink: 0,
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: 'var(--color-primary)', opacity: 1 },
  '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': { bgcolor: 'var(--on-primary)' },
  '& .MuiSwitch-switchBase.Mui-focusVisible .MuiSwitch-thumb': { outline: '2px solid var(--color-primary)', outlineOffset: '2px' },
  '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': { opacity: 0.5 },
};
const SEGMENT_SX = {
  flexShrink: 0,
  '& .MuiToggleButton-root': {
    ...FOCUS_SX,
    textTransform: 'none', minHeight: 40, px: 1.5, gap: 0.5,
    color: 'var(--text-secondary)', borderColor: 'var(--divider)', fontWeight: 'var(--fw-medium)',
    '&.Mui-selected': {
      bgcolor: 'var(--color-primary-soft)', color: 'var(--color-primary-dark)', fontWeight: 'var(--fw-bold)',
      '&:hover': { bgcolor: 'var(--color-primary-soft)' },
    },
  },
};
const SR_ONLY_SX = {
  position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap',
};
const FOCUS_TITLE_SX = {
  borderRadius: 'var(--radius-sm)',
  '&:focus': { outline: 'none' },
  '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: '3px' },
};
// Folga do aviso de erro do container (Snackbar) — mesma régua de Gestão de Acessos.
const FOLGA_AVISO = 'calc(var(--space-4) * 2 + var(--space-2))';

/* ── Movimento (sempre com a saída do prefers-reduced-motion) ─────────── */
const rise = keyframes`from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; }`;
const pop = keyframes`0% { transform: scale(.55); } 60% { transform: scale(1.14); } 100% { transform: scale(1); }`;
const glow = keyframes`0% { box-shadow: 0 0 0 0 var(--color-primary-light); } 100% { box-shadow: 0 0 0 14px transparent; }`;
const pulse = keyframes`0%, 100% { opacity: 1; } 50% { opacity: .3; }`;
const NO_MOTION = '@media (prefers-reduced-motion: reduce)';

/* ── Foco e rolagem (só comportamento de UI; mesma regra de Gestão de Acessos) ── */
const semMovimento = () => {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return true; }
};
const focoPerdido = () => {
  const ae = typeof document !== 'undefined' ? document.activeElement : null;
  return !ae || ae === document.body || ae === document.documentElement || ae.isConnected === false;
};
function levarFoco(el, mostrar = 'nunca') {
  if (!el || typeof el.focus !== 'function') return;
  el.focus({ preventScroll: true });
  let rolar = mostrar === 'sempre';
  if (mostrar === 'teclado') {
    try { rolar = el.matches(':focus-visible'); } catch { rolar = false; }
  }
  if (rolar && typeof el.scrollIntoView === 'function') {
    el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: semMovimento() ? 'auto' : 'smooth' });
  }
}

/* ── Ícones (por enum numérico do tipo; por rótulo quando o vm só traz o texto, com reserva) ── */
const TYPE_ICON_BY_VALUE = {
  1: ShortTextRounded, 2: NotesRounded, 3: PinRounded, 4: PaymentsRounded, 5: CalendarTodayRounded,
  6: ScheduleRounded, 7: RadioButtonCheckedRounded, 8: ChecklistRounded, 9: ThumbsUpDownRounded, 10: TaskAltRounded,
  11: PhoneRounded, 12: AlternateEmailRounded, 13: BadgeRounded, 14: BusinessRounded, 15: MarkunreadMailboxRounded,
};
const TYPE_ICON_BY_LABEL = {
  'Texto curto': ShortTextRounded, 'Texto longo': NotesRounded, 'Número': PinRounded, 'Valor em reais': PaymentsRounded,
  'Data': CalendarTodayRounded, 'Hora': ScheduleRounded, 'Escolha única': RadioButtonCheckedRounded,
  'Múltipla escolha': ChecklistRounded, 'Sim ou não': ThumbsUpDownRounded, 'Aceite': TaskAltRounded,
  'Telefone': PhoneRounded, 'E-mail': AlternateEmailRounded, 'CPF': BadgeRounded, 'CNPJ': BusinessRounded,
  'CEP': MarkunreadMailboxRounded,
};
const GROUP_ICON = { text: ShortTextRounded, numbers: PinRounded, choices: ChecklistRounded, contact: PhoneRounded };
const BASIC_ICON = { 1: CakeRounded, 2: BadgeRounded, 3: WcRounded, 4: HomeRounded };
const FILTER_ICON = { services: DesignServicesRounded, professionals: PersonRounded, places: PlaceRounded };
const AUDIENCE_ICONS = [DesignServicesRounded, PersonRounded, PlaceRounded];
const typeIcon = (label) => TYPE_ICON_BY_LABEL[label] || HelpOutlineRounded;

/* ── Pastilha (o chip das outras telas) ───────────────────────────────── */
const TONES = {
  neutral: { bg: 'var(--bg-page)', border: 'var(--divider)', fg: 'var(--text-secondary)' },
  primary: { bg: 'var(--color-primary-soft)', border: 'var(--color-primary-soft)', fg: 'var(--color-primary-dark)' },
  info: { bg: 'var(--tint-info-bg)', border: 'var(--tint-info-border)', fg: 'var(--tint-info-fg)' },
  success: { bg: 'var(--tint-success-bg)', border: 'var(--tint-success-border)', fg: 'var(--tint-success-fg)' },
  warning: { bg: 'var(--tint-warning-bg)', border: 'var(--tint-warning-border)', fg: 'var(--tint-warning-fg)' },
  error: { bg: 'var(--tint-error-bg)', border: 'var(--tint-error-border)', fg: 'var(--tint-error-fg)' },
};
const toneOf = (status) => (TONES[status] ? status : 'neutral');

function Pill({ icon, children, tone = 'neutral', size = 'sm', sx }) {
  const t = TONES[tone] || TONES.neutral;
  const big = size === 'md';
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.625, minWidth: 0, maxWidth: '100%',
        bgcolor: t.bg, border: '1px solid', borderColor: t.border, color: t.fg,
        borderRadius: 999, px: big ? 1.25 : 1.125, py: big ? 0.5 : '3px',
        ...sx,
      }}
    >
      {icon && <Box component="span" aria-hidden="true" sx={{ display: 'flex', flexShrink: 0, '& svg': { fontSize: big ? 18 : 14 } }}>{icon}</Box>}
      <Typography
        component="span"
        variant={big ? 'body2' : 'caption'}
        sx={{ color: 'inherit', fontWeight: big ? 'var(--fw-bold)' : 'var(--fw-medium)', overflowWrap: 'anywhere', lineHeight: 1.3 }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function Hint({ tone = 'info', children, role }) {
  if (!children) return null;
  const t = TONES[tone] || TONES.info;
  const Icon = tone === 'warning' ? WarningAmberRounded : tone === 'error' ? ErrorOutlineRounded : InfoOutlined;
  return (
    <Box
      role={role}
      sx={{
        display: 'flex', alignItems: 'flex-start', gap: 1,
        bgcolor: t.bg, border: '1px solid', borderColor: t.border,
        borderRadius: 'var(--radius-sm)', px: 1.5, py: 1,
      }}
    >
      <Icon aria-hidden="true" sx={{ fontSize: 18, color: t.fg, flexShrink: 0, mt: '1px' }} />
      <Typography variant="caption" sx={{ color: t.fg, lineHeight: 1.45, overflowWrap: 'anywhere' }}>{children}</Typography>
    </Box>
  );
}

function Spinner({ label }) {
  return <CircularProgress size={16} aria-label={label} sx={{ color: 'inherit' }} />;
}

function IconDot({ icon, tone = 'neutral', size = 40, sx }) {
  const t = {
    neutral: { bg: 'var(--bg-page)', fg: 'var(--text-secondary)' },
    primary: { bg: 'var(--color-primary-soft)', fg: 'var(--color-primary-dark)' },
    solid: { bg: 'var(--color-primary)', fg: 'var(--on-primary)' },
    warning: { bg: 'var(--tint-warning-bg)', fg: 'var(--tint-warning-fg)' },
    info: { bg: 'var(--tint-info-bg)', fg: 'var(--tint-info-fg)' },
  }[tone] || { bg: 'var(--bg-page)', fg: 'var(--text-secondary)' };
  return (
    <Box
      aria-hidden="true"
      sx={{
        width: size, height: size, borderRadius: '50%', flexShrink: 0,
        bgcolor: t.bg, color: t.fg, display: 'flex', alignItems: 'center', justifyContent: 'center',
        ...sx,
      }}
    >
      {icon}
    </Box>
  );
}

/* ── "Aparece em": a frase do vm desenhada como trilha ────────────────── */
// Só desenha em pedaços o que o vm escreveu ("Aparece em: A · B · C"). Se a frase não tiver esse formato,
// mostra como veio. O leitor de tela sempre recebe a frase inteira.
function parseAudience(label) {
  if (!label) return null;
  const i = label.indexOf(':');
  if (i < 0) return null;
  const prefix = label.slice(0, i).trim();
  const parts = label.slice(i + 1).split('·').map((s) => s.trim()).filter(Boolean);
  return parts.length ? { prefix, parts } : null;
}
const audienceTone = (part) => (/^nenhu/i.test(part) ? 'warning' : /^(tod[oa]s|qualquer)\b/i.test(part) ? 'neutral' : 'primary');

function AudienceTrail({ label, live = false, big = false }) {
  const p = parseAudience(label);
  if (!label) return null;
  if (!p) return <Typography variant="body2" sx={{ color: 'var(--text-secondary)', overflowWrap: 'anywhere' }}>{label}</Typography>;
  return (
    <Box sx={{ position: 'relative', minWidth: 0 }}>
      <Box component="span" sx={SR_ONLY_SX}>{label}</Box>
      <Box
        aria-hidden="true"
        sx={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: big ? 0.875 : 0.625,
          ...(live && {
            borderRadius: 'var(--radius-sm)',
            animation: `${glow} .7s ease-out`,
            [NO_MOTION]: { animation: 'none' },
          }),
        }}
      >
        <Typography
          component="span"
          variant={big ? 'overline' : 'caption'}
          sx={{ color: 'var(--text-secondary)', fontWeight: 'var(--fw-bold)', mr: 0.25, lineHeight: 1.3 }}
        >
          {p.prefix}
        </Typography>
        {p.parts.map((part, i) => {
          const Icon = p.parts.length === AUDIENCE_ICONS.length ? AUDIENCE_ICONS[i] : null;
          return (
            <Pill
              key={`${i}-${part}`}
              icon={Icon ? <Icon /> : null}
              tone={audienceTone(part)}
              size={big ? 'md' : 'sm'}
              sx={live ? { animation: `${rise} .35s ${i * 70}ms both`, [NO_MOTION]: { animation: 'none' } } : undefined}
            >
              {part}
            </Pill>
          );
        })}
      </Box>
    </Box>
  );
}

/* ── Blocos de estado ─────────────────────────────────────────────────── */
function StateBlock({ icon, title, description, action }) {
  return (
    <Box sx={{ ...CARD_SX, px: 3, py: { xs: 5, sm: 7 }, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
      <Box sx={{ width: 64, height: 64, borderRadius: '50%', mb: 2, bgcolor: 'var(--bg-page)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {icon}
      </Box>
      <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 'var(--fw-bold)', color: 'var(--text-primary)', mb: 0.5 }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" sx={{ color: 'var(--text-secondary)', maxWidth: 440, lineHeight: 1.5 }}>{description}</Typography>
      )}
      {action && <Box sx={{ mt: 2.5 }}>{action}</Box>}
    </Box>
  );
}

const BONE = { bgcolor: 'var(--divider)', transform: 'none' };

/* ════════════════════════════════════════════════════════════════════════
   CAMPO DE FORMULÁRIO — um BookingField desenhado. Usado na prévia, no agendamento manual e nas respostas.
   Rótulo SEMPRE fora do campo (rótulo de 200 caracteres não cabe num label flutuante).
   ════════════════════════════════════════════════════════════════════════ */
const GROUP_KINDS = ['radio', 'multi', 'yesNo', 'consent', 'address'];
const WIDE_KINDS = ['textarea', 'radio', 'multi', 'consent', 'address'];
const isWideField = (f) => WIDE_KINDS.includes(f?.inputKind) || (f?.label || '').length > 70;

function FieldShell({ id, labelId, descId, label, group, helpText, error, requiredMark, children, placeholderLabel }) {
  return (
    <Box role={group ? 'group' : undefined} aria-labelledby={group ? labelId : undefined} aria-describedby={group ? descId : undefined} sx={{ minWidth: 0 }}>
      <Typography
        id={labelId}
        component={group ? 'div' : 'label'}
        htmlFor={group ? undefined : id}
        variant="subtitle2"
        sx={{
          display: 'block', color: placeholderLabel ? 'var(--text-disabled)' : 'var(--text-primary)',
          overflowWrap: 'anywhere', lineHeight: 1.35, mb: 0.75, textWrap: 'pretty',
        }}
      >
        {label}
      </Typography>
      {children}
      <Box id={descId}>
        {helpText && (
          <Typography variant="caption" sx={{ color: 'var(--text-secondary)', mt: 0.75, lineHeight: 1.4, overflowWrap: 'anywhere' }}>{helpText}</Typography>
        )}
        {error && (
          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75, alignItems: 'flex-start' }}>
            <ErrorRounded aria-hidden="true" sx={{ fontSize: 16, color: 'var(--color-error)', mt: '1px', flexShrink: 0 }} />
            <Typography variant="caption" sx={{ color: 'var(--color-error)', fontWeight: 'var(--fw-medium)', lineHeight: 1.4 }}>{error}</Typography>
          </Box>
        )}
        {requiredMark && (
          <Typography variant="caption" sx={{ color: 'var(--text-secondary)', mt: 0.75, display: 'flex', gap: 0.5, alignItems: 'baseline', lineHeight: 1.4 }}>
            <Box component="span" aria-hidden="true" sx={{ color: 'var(--color-primary-dark)', fontWeight: 'var(--fw-bold)' }}>*</Box>
            {requiredMark}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// Pastilhas de escolha: radio (uma), multi (várias), sim/não. Alvo ≥40 px, marca + texto (nunca só cor).
function ChoicePills({ opts, isOn, onToggle, multi, labelId, descId, disabled }) {
  return (
    <Box
      role={multi ? 'group' : 'radiogroup'}
      aria-labelledby={labelId}
      aria-describedby={descId}
      sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}
    >
      {opts.map((o) => {
        const on = !!isOn(o.value);
        const Mark = multi
          ? (on ? CheckBoxRounded : CheckBoxOutlineBlankRounded)
          : (on ? RadioButtonCheckedRounded : RadioButtonUncheckedRounded);
        return (
          <ButtonBase
            key={String(o.value)}
            role={multi ? 'checkbox' : 'radio'}
            aria-checked={on}
            disabled={disabled}
            onClick={() => onToggle(o.value)}
            sx={{
              ...FOCUS_SX,
              minHeight: 40, px: 1.5, py: 0.75, gap: 0.75, borderRadius: 999, maxWidth: '100%',
              justifyContent: 'flex-start', textAlign: 'left',
              border: '1px solid', borderColor: on ? 'var(--color-primary)' : 'var(--divider)',
              bgcolor: on ? 'var(--color-primary-soft)' : 'var(--surface)',
              color: on ? 'var(--color-primary-dark)' : 'var(--text-primary)',
              transition: 'background-color .15s, border-color .15s',
              '&:hover': { borderColor: 'var(--color-primary)' },
              '&.Mui-disabled': { color: 'var(--text-disabled)' },
            }}
          >
            <Mark aria-hidden="true" sx={{ fontSize: 18, flexShrink: 0, color: on ? 'var(--color-primary-dark)' : 'var(--text-secondary)' }} />
            <Typography component="span" variant="body2" sx={{ color: 'inherit', fontWeight: on ? 'var(--fw-bold)' : 'var(--fw-medium)', overflowWrap: 'anywhere' }}>
              {o.label}
            </Typography>
          </ButtonBase>
        );
      })}
    </Box>
  );
}

// Endereço: as partes que o vm manda, numa grade que se arruma pela largura do próprio bloco.
const ADDRESS_SPAN = {
  zipCode: [2, 2], street: [2, 4], number: [1, 1], complement: [1, 2], neighborhood: [2, 3], city: [1, 5], state: [1, 1],
};
function AddressInput({ id, parts, value, onChange, disabled }) {
  const val = value && typeof value === 'object' ? value : {};
  return (
    <Box sx={{ containerType: 'inline-size' }}>
      <Box
        sx={{
          display: 'grid', gap: 1.25, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          '@container (min-width: 520px)': { gridTemplateColumns: 'repeat(6, minmax(0, 1fr))' },
        }}
      >
        {(parts || []).map((p) => {
          const span = ADDRESS_SPAN[p.key] || [2, 6];
          const pid = `${id}-${p.key}`;
          return (
            <Box
              key={p.key}
              sx={{
                minWidth: 0, gridColumn: `span ${span[0]}`,
                '@container (min-width: 520px)': { gridColumn: `span ${span[1]}` },
              }}
            >
              <Typography component="label" htmlFor={pid} variant="caption" sx={{ color: 'var(--text-secondary)', fontWeight: 'var(--fw-medium)', mb: 0.5 }}>
                {p.label}
              </Typography>
              <TextField
                id={pid}
                fullWidth
                disabled={disabled}
                value={val[p.key] ?? ''}
                onChange={(e) => onChange({ ...val, [p.key]: e.target.value })}
                inputProps={{ inputMode: p.key === 'zipCode' || p.key === 'number' ? 'numeric' : undefined }}
                sx={FIELD_SX}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

const TEXT_LIKE = {
  text: { type: 'text' },
  phone: { type: 'tel', inputMode: 'tel' },
  email: { type: 'email', inputMode: 'email' },
  cpf: { type: 'text', inputMode: 'numeric' },
  cnpj: { type: 'text', inputMode: 'numeric' },
  cep: { type: 'text', inputMode: 'numeric' },
};

function FieldInput({ field, onChange, disabled, idPrefix = 'cf', placeholderLabel }) {
  const f = field || {};
  const id = `${idPrefix}-${f.id || 'campo'}`;
  const labelId = `${id}-rotulo`;
  const descId = `${id}-ajuda`;
  const kind = f.inputKind;
  const v = f.value;
  const set = (val) => onChange?.(f.id, val);
  const err = !!f.error;
  // Opção sem id (a prévia da criação ainda não tem) recebe uma chave local só para desenhar.
  const opts = (f.options || []).map((o, i) => ({ value: o.id || `__${i}`, label: o.label }));
  const common = { id, fullWidth: true, disabled, error: err, sx: FIELD_SX };
  const described = { 'aria-describedby': descId };

  let control;
  if (TEXT_LIKE[kind]) {
    control = (
      <TextField
        {...common}
        type={TEXT_LIKE[kind].type}
        value={v ?? ''}
        onChange={(e) => set(e.target.value)}
        inputProps={{ ...described, inputMode: TEXT_LIKE[kind].inputMode, maxLength: f.maxLength || undefined }}
      />
    );
  } else if (kind === 'textarea') {
    control = (
      <TextField
        {...common}
        multiline
        minRows={3}
        maxRows={10}
        value={v ?? ''}
        onChange={(e) => set(e.target.value)}
        inputProps={{ ...described, maxLength: f.maxLength || undefined }}
      />
    );
  } else if (kind === 'number' || kind === 'money') {
    control = (
      <TextField
        {...common}
        type="number"
        value={v ?? ''}
        onChange={(e) => set(e.target.value)}
        inputProps={{ ...described, inputMode: 'decimal', min: f.min ?? undefined, max: f.max ?? undefined }}
        InputProps={{
          startAdornment: kind === 'money' ? <InputAdornment position="start">R$</InputAdornment> : undefined,
          endAdornment: f.unit ? <InputAdornment position="end">{f.unit}</InputAdornment> : undefined,
        }}
        sx={{ ...FIELD_SX, maxWidth: { sm: 280 } }}
      />
    );
  } else if (kind === 'date' || kind === 'time') {
    control = (
      <TextField
        {...common}
        type={kind}
        value={v ?? ''}
        onChange={(e) => set(e.target.value)}
        inputProps={{ ...described, min: f.min ?? undefined, max: f.max ?? undefined }}
        sx={{ ...FIELD_SX, maxWidth: { sm: 280 } }}
      />
    );
  } else if (kind === 'select') {
    control = (
      <TextField
        {...common}
        id={undefined}
        select
        value={v ?? ''}
        onChange={(e) => set(e.target.value)}
        SelectProps={{
          displayEmpty: true,
          SelectDisplayProps: { id, 'aria-labelledby': labelId, 'aria-describedby': descId },
          renderValue: (val) => (val !== '' && val != null
            ? (opts.find((o) => o.value === val)?.label ?? String(val))
            : <Box component="span" sx={{ color: 'var(--text-secondary)' }}>Escolha uma opção</Box>),
          MenuProps: { PaperProps: { sx: { maxHeight: 360 } } },
        }}
      >
        {opts.map((o) => (
          <MenuItem key={o.value} value={o.value} sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{o.label}</MenuItem>
        ))}
      </TextField>
    );
  } else if (kind === 'radio') {
    control = <ChoicePills opts={opts} isOn={(x) => v === x} onToggle={(x) => set(x)} labelId={labelId} descId={descId} disabled={disabled} />;
  } else if (kind === 'multi') {
    const arr = Array.isArray(v) ? v : [];
    control = (
      <ChoicePills
        multi
        opts={opts}
        isOn={(x) => arr.includes(x)}
        onToggle={(x) => set(arr.includes(x) ? arr.filter((y) => y !== x) : [...arr, x])}
        labelId={labelId}
        descId={descId}
        disabled={disabled}
      />
    );
  } else if (kind === 'yesNo') {
    control = (
      <ChoicePills
        opts={[{ value: true, label: f.yesLabel || 'Sim' }, { value: false, label: f.noLabel || 'Não' }]}
        isOn={(x) => v === x}
        onToggle={(x) => set(x)}
        labelId={labelId}
        descId={descId}
        disabled={disabled}
      />
    );
  } else if (kind === 'consent') {
    control = (
      <Box
        component="label"
        htmlFor={id}
        sx={{
          display: 'flex', gap: 1, alignItems: 'flex-start', p: 1.25, cursor: disabled ? 'default' : 'pointer',
          border: '1px solid', borderColor: v ? 'var(--color-primary)' : 'var(--divider)', borderRadius: 'var(--radius-sm)',
          bgcolor: v ? 'var(--color-primary-soft)' : 'var(--surface)', transition: 'background-color .15s, border-color .15s',
          '&:hover': { borderColor: 'var(--color-primary)' },
        }}
      >
        <Checkbox
          id={id}
          checked={!!v}
          disabled={disabled}
          onChange={(e) => set(e.target.checked)}
          inputProps={described}
          sx={{ p: 0.5, mt: '-3px', color: 'var(--text-secondary)', '&.Mui-checked': { color: 'var(--color-primary)' }, ...FOCUS_SX }}
        />
        <Typography variant="body2" sx={{ color: 'var(--text-primary)', lineHeight: 1.5, overflowWrap: 'anywhere', pt: '2px' }}>
          {f.consentText}
        </Typography>
      </Box>
    );
  } else if (kind === 'address') {
    control = <AddressInput id={id} parts={f.addressParts} value={v} onChange={set} disabled={disabled} />;
  } else {
    control = (
      <TextField {...common} value={v ?? ''} onChange={(e) => set(e.target.value)} inputProps={described} />
    );
  }

  return (
    <FieldShell
      id={id}
      labelId={labelId}
      descId={descId}
      label={f.label}
      placeholderLabel={placeholderLabel}
      group={GROUP_KINDS.includes(kind)}
      helpText={f.helpText}
      error={f.error}
      requiredMark={f.requiredMark}
    >
      {control}
    </FieldShell>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   BLOCOS — vivem dentro de telas que já existem (dialog de ~900 px; tela cheia no celular).
   ════════════════════════════════════════════════════════════════════════ */
function BlockSection({ id, title, action, children }) {
  return (
    <Box component="section" aria-labelledby={id} sx={{ color: 'var(--text-primary)', borderTop: '1px solid', borderColor: 'var(--divider)', pt: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <QuizRounded aria-hidden="true" sx={{ fontSize: 20, color: 'var(--color-primary-dark)' }} />
        <Typography id={id} variant="subtitle1" component="h3" sx={{ fontWeight: 'var(--fw-bold)', flex: '1 1 auto', minWidth: 0 }}>
          {title}
        </Typography>
        {action}
      </Box>
      {children}
    </Box>
  );
}

function BlockLoading({ id, title, rows = 2, label }) {
  return (
    <BlockSection id={id} title={title}>
      <Box role="status" aria-live="polite" aria-busy="true">
        <Box component="span" sx={SR_ONLY_SX}>{label}</Box>
        <Box aria-hidden="true" sx={{ display: 'grid', gap: 2.25, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' } }}>
          {Array.from({ length: rows * 2 }).map((_, i) => (
            <Box key={i}>
              <Skeleton variant="rounded" animation="wave" height={12} width={`${45 + ((i * 17) % 35)}%`} sx={{ ...BONE, mb: 1 }} />
              <Skeleton variant="rounded" animation="wave" height={42} sx={{ ...BONE, borderRadius: 'var(--radius-sm)' }} />
            </Box>
          ))}
        </Box>
      </Box>
    </BlockSection>
  );
}

// Erro de bloco: calmo, ao lado do resto da tela — nunca impede agendar.
function BlockError({ id, title, message, onRetry }) {
  return (
    <BlockSection id={id} title={title}>
      <Box
        role="status"
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap',
          bgcolor: 'var(--tint-neutral-bg)', border: '1px solid', borderColor: 'var(--tint-neutral-border)',
          borderRadius: 'var(--radius-sm)', px: 1.5, py: 1,
        }}
      >
        <ErrorOutlineRounded aria-hidden="true" sx={{ fontSize: 20, color: 'var(--color-warning)', flexShrink: 0 }} />
        <Typography variant="body2" sx={{ color: 'var(--text-primary)', flex: '1 1 220px', lineHeight: 1.45 }}>{message}</Typography>
        <Button onClick={() => onRetry?.()} startIcon={<ReplayRounded />} sx={{ ...QUIET_BTN_SX, minHeight: 40 }}>
          Tentar de novo
        </Button>
      </Box>
    </BlockSection>
  );
}

function FieldGrid({ fields, a, idPrefix, disabled }) {
  return (
    <Box sx={{ containerType: 'inline-size' }}>
      <Box
        sx={{
          display: 'grid', gap: 2.25, gridTemplateColumns: 'minmax(0, 1fr)',
          '@container (min-width: 560px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
        }}
      >
        {fields.map((f) => (
          <Box key={f.id} sx={{ minWidth: 0, gridColumn: isWideField(f) ? '1 / -1' : 'auto' }}>
            <FieldInput field={f} onChange={(fid, val) => a.onFieldChange?.(fid, val)} idPrefix={idPrefix} disabled={disabled} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

const bookingHasContent = (b) => !!b && (b.viewState !== 'ready' || (b.fields || []).length > 0);

export function BookingQuestions({ vm, actions }) {
  const b = vm || {};
  const a = actions || {};
  if (!bookingHasContent(b)) return null; // sem perguntas: não desenha nada, nem título
  const id = 'bq-titulo';
  if (b.viewState === 'loading') return <BlockLoading id={id} title={b.title} label="Carregando perguntas…" />;
  if (b.viewState === 'error') return <BlockError id={id} title={b.title} message={b.errorMessage} onRetry={a.onRetry} />;
  return (
    <BlockSection id={id} title={b.title}>
      <FieldGrid fields={b.fields || []} a={a} idPrefix="bq" />
    </BlockSection>
  );
}

/* ── Divergência: uma pequena decisão, não um alarme ──────────────────── */
function Divergence({ d, a, showTitle }) {
  // Com a mensagem de erro (409), os botões voltam a valer: é a hora de decidir de novo.
  const busy = !!d.resolving && !d.errorMessage;
  return (
    <Box
      role="group"
      aria-label={d.title}
      aria-busy={busy || undefined}
      sx={{
        display: 'flex', flexDirection: 'column', gap: 1,
        bgcolor: 'var(--tint-info-bg)', border: '1px solid', borderColor: 'var(--tint-info-border)',
        borderRadius: 'var(--radius-sm)', px: 1.5, py: 1.25,
      }}
    >
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <CompareArrowsRounded aria-hidden="true" sx={{ fontSize: 20, color: 'var(--tint-info-fg)', flexShrink: 0, mt: '1px' }} />
        <Box sx={{ minWidth: 0 }}>
          {showTitle && (
            <Typography variant="subtitle2" sx={{ color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>{d.title}</Typography>
          )}
          <Typography variant="body2" sx={{ color: 'var(--text-primary)', lineHeight: 1.45, overflowWrap: 'anywhere', mt: showTitle ? 0.25 : 0 }}>
            {d.message}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', pl: { sm: 3.5 } }}>
        <Button
          onClick={() => a.onApply?.(d.valueId)}
          disabled={busy}
          startIcon={busy ? <Spinner label="Aplicando" /> : <CheckRounded />}
          sx={{ ...OUTLINE_BTN_SX, minHeight: 40, px: 1.75, bgcolor: 'var(--surface)' }}
        >
          {d.applyLabel}
        </Button>
        <Button onClick={() => a.onIgnore?.(d.valueId)} disabled={busy} sx={{ ...QUIET_BTN_SX, minHeight: 40, bgcolor: 'var(--surface)' }}>
          {d.ignoreLabel}
        </Button>
      </Box>
      {d.errorMessage && (
        <Box role="alert" sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', pl: { sm: 3.5 } }}>
          <ErrorRounded aria-hidden="true" sx={{ fontSize: 16, color: 'var(--color-error)', mt: '1px', flexShrink: 0 }} />
          <Typography variant="caption" sx={{ color: 'var(--color-error)', fontWeight: 'var(--fw-medium)', lineHeight: 1.4 }}>{d.errorMessage}</Typography>
        </Box>
      )}
      <Box role="status" aria-live="polite" sx={SR_ONLY_SX}>{busy ? 'Aplicando…' : ''}</Box>
    </Box>
  );
}

// Par rótulo/valor das respostas e da ficha.
function ValueCell({ label, value, empty, children }) {
  return (
    <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box>
        <Typography component="dt" variant="caption" sx={{ color: 'var(--text-secondary)', fontWeight: 'var(--fw-medium)', overflowWrap: 'anywhere', lineHeight: 1.35 }}>
          {label}
        </Typography>
        <Typography
          component="dd"
          variant="body1"
          sx={{
            m: 0, mt: 0.25, overflowWrap: 'anywhere', lineHeight: 1.4,
            color: empty ? 'var(--text-secondary)' : 'var(--text-primary)',
            fontWeight: empty ? 'var(--fw-regular)' : 'var(--fw-medium)',
            fontStyle: empty ? 'italic' : 'normal',
          }}
        >
          {value}
        </Typography>
      </Box>
      {children}
    </Box>
  );
}

const VALUES_GRID_SX = {
  m: 0, display: 'grid', gap: 2.25, gridTemplateColumns: 'minmax(0, 1fr)',
  '@container (min-width: 560px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
};

const answersHaveContent = (b) => !!b && (b.viewState !== 'ready' || (b.answers || []).length > 0 || (b.unanswered || []).length > 0);

export function AppointmentAnswers({ vm, actions }) {
  const b = vm || {};
  const a = actions || {};
  const [editingLocal, setEditingLocal] = useState(false); // UI: "editar respostas"
  if (!answersHaveContent(b)) return null;
  const id = 'ar-titulo';
  if (b.viewState === 'loading') return <BlockLoading id={id} title={b.title} label="Carregando respostas…" />;
  if (b.viewState === 'error') return <BlockError id={id} title={b.title} message={b.errorMessage} onRetry={a.onRetry} />;

  const answers = b.answers || [];
  const unanswered = b.unanswered || [];
  // Com algo alterado, o modo de edição não fecha sozinho: só o salvar (o contrato não tem "descartar").
  const editing = editingLocal || !!b.isDirty;

  const action = answers.length > 0 && (!editing || !b.isDirty) ? (
    <Button
      onClick={() => setEditingLocal((x) => !x)}
      startIcon={editing ? <CheckRounded /> : <EditRounded />}
      aria-pressed={editing}
      sx={{ ...QUIET_BTN_SX, minHeight: 40 }}
    >
      {editing ? 'Concluir' : 'Editar respostas'}
    </Button>
  ) : null;

  return (
    <BlockSection id={id} title={b.title} action={action}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {answers.length > 0 && (
          editing ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
              <FieldGrid fields={answers.map((r) => r.field)} a={a} idPrefix="ar" disabled={b.saving} />
              {answers.filter((r) => r.divergence).map((r) => <Divergence key={r.id} d={r.divergence} a={a} showTitle />)}
            </Box>
          ) : (
            <Box sx={{ containerType: 'inline-size' }}>
              <Box component="dl" sx={VALUES_GRID_SX}>
                {answers.map((r) => (
                  <ValueCell key={r.id} label={r.label} value={r.valueText}>
                    {r.divergence && <Divergence d={r.divergence} a={a} />}
                  </ValueCell>
                ))}
              </Box>
            </Box>
          )
        )}

        {unanswered.length > 0 && (
          <Box
            sx={{
              border: '1px dashed', borderColor: 'var(--text-disabled)', borderRadius: 'var(--radius-sm)',
              p: { xs: 1.5, sm: 2 }, display: 'flex', flexDirection: 'column', gap: 1.75,
            }}
          >
            {b.unansweredLabel && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <EditRounded aria-hidden="true" sx={{ fontSize: 18, color: 'var(--text-secondary)' }} />
                <Typography variant="subtitle2" component="h4" sx={{ color: 'var(--text-secondary)' }}>{b.unansweredLabel}</Typography>
              </Box>
            )}
            <FieldGrid fields={unanswered} a={a} idPrefix="ar-novo" disabled={b.saving} />
          </Box>
        )}

        {b.isDirty && (
          <Box
            sx={{
              display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 1.5,
              borderTop: '1px solid', borderColor: 'var(--divider)',
              animation: `${rise} .25s ease-out`, [NO_MOTION]: { animation: 'none' },
            }}
          >
            <Button
              onClick={() => a.onSave?.()}
              disabled={b.saving}
              disableElevation
              startIcon={b.saving ? <Spinner label="Salvando" /> : <CheckRounded />}
              sx={{ ...PRIMARY_BTN_SX, width: { xs: '100%', sm: 'auto' } }}
            >
              {b.saving ? 'Salvando…' : 'Salvar respostas'}
            </Button>
          </Box>
        )}
      </Box>
    </BlockSection>
  );
}

const personHasContent = (b) => !!b && (b.viewState !== 'ready' || (b.values || []).length > 0 || (b.divergences || []).length > 0);

export function PersonCustomFields({ vm, actions }) {
  const b = vm || {};
  const a = actions || {};
  if (!personHasContent(b)) return null;
  const id = 'pf-titulo';
  if (b.viewState === 'loading') return <BlockLoading id={id} title={b.title} label="Carregando dados…" rows={1} />;
  if (b.viewState === 'error') return <BlockError id={id} title={b.title} message={b.errorMessage} onRetry={a.onRetry} />;
  const values = b.values || [];
  const divs = b.divergences || [];
  // A divergência entra no próprio dado quando o título é o mesmo rótulo; senão, logo abaixo da lista.
  const attached = new Set();
  const forRow = (label) => divs.filter((d) => {
    if (d.title !== label || attached.has(d.valueId)) return false;
    attached.add(d.valueId);
    return true;
  });
  const cells = values.map((r) => ({ r, ds: forRow(r.label) }));
  const loose = divs.filter((d) => !attached.has(d.valueId));
  return (
    <BlockSection id={id} title={b.title}>
      <Box sx={{ containerType: 'inline-size', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box component="dl" sx={VALUES_GRID_SX}>
          {cells.map(({ r, ds }) => (
            <ValueCell key={r.fieldId} label={r.label} value={r.valueText} empty={r.isEmpty}>
              {ds.map((d) => <Divergence key={d.valueId} d={d} a={a} />)}
            </ValueCell>
          ))}
        </Box>
        {loose.map((d) => <Divergence key={d.valueId} d={d} a={a} showTitle />)}
      </Box>
    </BlockSection>
  );
}

// Só no estúdio: o bloco sozinho, numa moldura da largura em que ele vive (~900 px).
function BlockStage({ vm, actions }) {
  const s = vm.surface;
  const b = vm.block;
  const visible = s === 'booking' ? bookingHasContent(b) : s === 'answers' ? answersHaveContent(b) : personHasContent(b);
  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', ...CARD_SX, p: { xs: 2, sm: 3 }, '& > section': { borderTop: 0, pt: 0 } }}>
      {s === 'booking' && <BookingQuestions vm={b} actions={actions} />}
      {s === 'answers' && <AppointmentAnswers vm={b} actions={actions} />}
      {s === 'person' && <PersonCustomFields vm={b} actions={actions} />}
      {!visible && (
        <Typography variant="caption" sx={{ color: 'var(--text-secondary)', py: 2, textAlign: 'center' }}>
          Nesta situação o bloco não desenha nada: a tela que o recebe segue como se ele não existisse.
        </Typography>
      )}
    </Box>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   TELA
   ════════════════════════════════════════════════════════════════════════ */

/* ── Resumo + uso do plano ────────────────────────────────────────────── */
function PlanMeter({ p }) {
  const tone = p.status === 'error' ? 'error' : p.status === 'warning' ? 'warning' : 'primary';
  const fill = tone === 'error' ? 'var(--tint-error-fg)' : tone === 'warning' ? 'var(--tint-warning-fg)' : 'var(--color-primary)';
  const limit = Math.max(1, Number(p.limit) || 1);
  const used = Math.min(limit, Math.max(0, Number(p.used) || 0));
  const segmented = limit <= 12;
  return (
    <Box sx={{ ...CARD_SX, p: 1.5, flex: '1 1 260px', maxWidth: { md: 400 }, display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {tone === 'error' && <LockOutlined aria-hidden="true" sx={{ fontSize: 16, color: fill }} />}
        <Typography variant="subtitle2" sx={{ color: 'var(--text-primary)' }}>{p.label}</Typography>
      </Box>
      <Box aria-hidden="true" sx={{ display: 'flex', gap: 0.5 }}>
        {segmented
          ? Array.from({ length: limit }).map((_, i) => (
            <Box key={i} sx={{ flex: 1, height: 8, borderRadius: 999, bgcolor: i < used ? fill : 'var(--divider)', transition: 'background-color .3s' }} />
          ))
          : (
            <Box sx={{ flex: 1, height: 8, borderRadius: 999, bgcolor: 'var(--divider)', overflow: 'hidden' }}>
              <Box sx={{ width: `${(used / limit) * 100}%`, height: '100%', bgcolor: fill }} />
            </Box>
          )}
      </Box>
      <Typography variant="caption" sx={{ color: tone === 'error' ? 'var(--tint-error-fg)' : 'var(--text-secondary)', lineHeight: 1.4 }}>
        {p.message}
      </Typography>
    </Box>
  );
}

function SummaryBar({ vm, onAttention }) {
  const s = vm.summary || {};
  const p = vm.planUsage;
  const warn = toneOf(s.status) === 'warning' || toneOf(s.status) === 'error' ? toneOf(s.status) : 'warning';
  return (
    <Box
      sx={{
        mb: 'var(--space-3)',
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 3, rowGap: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flex: '999 1 320px', minWidth: 0 }}>
        {s.activeLabel && <Pill icon={<CheckCircleRounded />} tone="primary" size="md">{s.activeLabel}</Pill>}
        {s.inactiveLabel && <Pill icon={<PauseCircleOutlineRounded />} size="md">{s.inactiveLabel}</Pill>}
        {s.attentionLabel && (
          <ButtonBase
            onClick={onAttention}
            aria-label={`${s.attentionLabel}. Ir até a pergunta.`}
            sx={{ ...FOCUS_SX, borderRadius: 999, '&:hover > span': { borderColor: 'var(--tint-warning-fg)' } }}
          >
            <Pill icon={<WarningAmberRounded />} tone={warn} size="md">
              {s.attentionLabel}
              <KeyboardArrowRightRounded aria-hidden="true" sx={{ fontSize: 18, verticalAlign: 'middle', ml: 0.25, mt: '-2px' }} />
            </Pill>
          </ButtonBase>
        )}
      </Box>
      {p && <PlanMeter p={p} />}
    </Box>
  );
}

/* ── Número da pergunta (o "degrau" do formulário) ────────────────────── */
function OrderBadge({ n, inactive, warn }) {
  return (
    <Box
      aria-hidden="true"
      sx={{
        width: 32, height: 32, borderRadius: '50%', flexShrink: 0, position: 'relative', zIndex: 1,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: warn ? 'var(--tint-warning-bg)' : inactive ? 'var(--surface)' : 'var(--color-primary-soft)',
        color: warn ? 'var(--tint-warning-fg)' : inactive ? 'var(--text-secondary)' : 'var(--color-primary-dark)',
        border: '1px solid', borderStyle: inactive ? 'dashed' : 'solid',
        borderColor: warn ? 'var(--tint-warning-border)' : inactive ? 'var(--text-disabled)' : 'var(--color-primary-soft)',
      }}
    >
      <Typography component="span" variant="subtitle2" sx={{ color: 'inherit', fontWeight: 'var(--fw-bold)' }}>{n}</Typography>
    </Box>
  );
}

/* ── Trocar rótulo — a única edição que existe ────────────────────────── */
function RenameEditor({ r, a, quiet, qid }) {
  const ref = useRef(null);
  const quietNaMontagem = useRef(quiet);
  useEffect(() => {
    const el = ref.current;
    if (quietNaMontagem.current || !el) return;
    el.focus({ preventScroll: true });
    try { el.setSelectionRange(el.value.length, el.value.length); } catch { /* sem seleção */ }
  }, []);
  const id = `rename-${qid}`;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography component="label" htmlFor={id} variant="caption" sx={{ color: 'var(--text-secondary)', fontWeight: 'var(--fw-bold)' }}>
        Novo rótulo
      </Typography>
      <TextField
        id={id}
        inputRef={ref}
        value={r.value ?? ''}
        onChange={(e) => a.onRenameChange?.(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (!r.saving) a.onRenameSave?.(); }
          if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); a.onRenameCancel?.(); }
        }}
        multiline
        maxRows={6}
        fullWidth
        error={!!r.error}
        disabled={r.saving}
        inputProps={{ maxLength: 200, 'aria-describedby': `${id}-ajuda` }}
        sx={FIELD_SX}
      />
      <Box id={`${id}-ajuda`}>
        {r.error ? (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
            <ErrorRounded aria-hidden="true" sx={{ fontSize: 16, color: 'var(--color-error)', mt: '1px' }} />
            <Typography variant="caption" sx={{ color: 'var(--color-error)', fontWeight: 'var(--fw-medium)' }}>{r.error}</Typography>
          </Box>
        ) : (
          <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
            Só o texto muda. O tipo, as regras e quem vê continuam como estão.
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <Button onClick={() => a.onRenameCancel?.()} disabled={r.saving} sx={{ ...QUIET_BTN_SX, flex: { xs: '1 1 auto', sm: '0 0 auto' } }}>
          Cancelar
        </Button>
        <Button
          onClick={() => a.onRenameSave?.()}
          disabled={r.saving}
          disableElevation
          startIcon={r.saving ? <Spinner label="Salvando" /> : <CheckRounded />}
          sx={{ ...PRIMARY_BTN_SX, flex: { xs: '1 1 auto', sm: '0 0 auto' } }}
        >
          {r.saving ? 'Salvando…' : 'Salvar rótulo'}
        </Button>
      </Box>
    </Box>
  );
}

/* ── Confirmação de apagar (mesma regra de foco/rolagem de Gestão de Acessos) ── */
function DeleteConfirm({ c, a, quiet, focusFlag }) {
  const boxRef = useRef(null);
  const quietNaMontagem = useRef(quiet);
  useEffect(() => {
    if (!quietNaMontagem.current) levarFoco(boxRef.current, 'sempre');
  }, []);
  const antes = useRef(c.deleting);
  useEffect(() => {
    if (antes.current === c.deleting) return;
    antes.current = c.deleting;
    const box = boxRef.current;
    if (box && (focoPerdido() || box.contains(document.activeElement))) levarFoco(box, 'nunca');
  }, [c.deleting]);
  const titleId = `del-${c.id}`;
  const bodyId = `del-${c.id}-texto`;
  const btn = { flex: { xs: '1 1 auto', sm: '0 0 auto' } };
  return (
    <Box
      ref={boxRef}
      tabIndex={-1}
      role="alertdialog"
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      aria-busy={c.deleting || undefined}
      onFocus={() => { if (focusFlag) focusFlag.current = true; }}
      onBlur={(e) => { if (focusFlag && !e.currentTarget.contains(e.relatedTarget)) focusFlag.current = false; }}
      sx={{
        mt: 1.75, bgcolor: 'var(--tint-error-bg)', border: '1px solid', borderColor: 'var(--tint-error-border)',
        borderRadius: 'var(--radius-sm)', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25,
        '&:focus': { outline: 'none' }, scrollMarginBottom: FOLGA_AVISO,
        animation: `${rise} .2s ease-out`, [NO_MOTION]: { animation: 'none' },
      }}
    >
      <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
        <DeleteOutlineRounded aria-hidden="true" sx={{ fontSize: 22, color: 'var(--tint-error-fg)', flexShrink: 0, mt: '1px' }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography id={titleId} variant="subtitle2" sx={{ color: 'var(--tint-error-fg)', overflowWrap: 'anywhere' }}>{c.title}</Typography>
          <Typography id={bodyId} variant="body2" sx={{ color: 'var(--text-primary)', mt: 0.5, lineHeight: 1.45 }}>{c.message}</Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <Button onClick={() => a.onCancelDelete?.()} disabled={c.deleting} sx={{ ...QUIET_BTN_SX, ...btn, bgcolor: 'var(--surface)' }}>
          Manter
        </Button>
        <Button
          onClick={() => a.onConfirmDelete?.()}
          disabled={c.deleting}
          startIcon={c.deleting ? <Spinner label="Apagando" /> : <DeleteOutlineRounded />}
          sx={{ ...DANGER_BTN_SX, ...btn }}
        >
          {c.deleting ? 'Apagando…' : c.confirmLabel}
        </Button>
      </Box>
      <Box role="status" aria-live="polite" sx={SR_ONLY_SX}>{c.deleting ? 'Apagando…' : ''}</Box>
    </Box>
  );
}

/* ── Cartão de pergunta ───────────────────────────────────────────────── */
function QuestionCard({ q, index, shownIndex, total, vm, a, quiet, cardRef, dragSx, handleProps, onMove, planMessage }) {
  const renaming = vm.rename && vm.rename.id === q.id ? vm.rename : null;
  const confirm = vm.confirmation && vm.confirmation.id === q.id ? vm.confirmation : null;
  const warnings = q.warnings || [];
  const orphan = warnings.some((w) => w.status === 'warning' || w.status === 'error');
  const inactive = !q.isActive;
  const toggleBlocked = inactive && !q.canActivate;
  const labelId = `q-${q.id}-rotulo`;
  const TypeIcon = typeIcon(q.typeLabel);
  const TargetIcon = /ficha/i.test(q.targetLabel || '') ? FolderSharedRounded : EventNoteRounded;
  const VisIcon = /equipe/i.test(q.visibilityLabel || '') && !/agenda/i.test(q.visibilityLabel || '') ? LockOutlined : VisibilityRounded;

  // Cancelou o apagar: se o foco estava na confirmação, ele volta para o botão de apagar.
  const delRef = useRef(null);
  const focoNaConfirmacao = useRef(false);
  const tinhaConfirmacao = useRef(!!confirm);
  useEffect(() => {
    const antes = tinhaConfirmacao.current;
    tinhaConfirmacao.current = !!confirm;
    if (antes && !confirm) {
      if (focoNaConfirmacao.current && focoPerdido()) levarFoco(delRef.current, 'teclado');
      focoNaConfirmacao.current = false;
    }
  }, [!!confirm]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box
      ref={cardRef}
      component="article"
      aria-labelledby={labelId}
      tabIndex={-1}
      sx={{
        ...CARD_SX,
        position: 'relative', zIndex: 1,
        p: { xs: 2, sm: 2.25 },
        bgcolor: inactive ? 'var(--bg-page)' : 'var(--surface)',
        borderStyle: inactive ? 'dashed' : 'solid',
        borderColor: confirm ? 'var(--tint-error-border)' : orphan ? 'var(--tint-warning-fg)' : inactive ? 'var(--text-disabled)' : 'var(--divider)',
        boxShadow: inactive ? 'none' : 'var(--shadow-card)',
        transition: 'transform .22s ease, box-shadow .15s, border-color .15s',
        '&:hover': inactive ? {} : { boxShadow: 'var(--shadow-hover)', borderColor: orphan ? 'var(--tint-warning-fg)' : 'var(--color-primary-light)' },
        '&:focus': { outline: 'none' },
        '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: '3px' },
        scrollMarginTop: 'var(--space-3)',
        ...dragSx,
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: '32px minmax(0, 1fr) auto', columnGap: { xs: 1.25, sm: 1.5 }, alignItems: 'start' }}>
        <OrderBadge n={(shownIndex ?? index) + 1} inactive={inactive} warn={orphan} />
        <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {renaming ? (
            <>
              <Box component="span" id={labelId} sx={SR_ONLY_SX}>{q.label}</Box>
              <RenameEditor r={renaming} a={a} quiet={quiet} qid={q.id} />
            </>
          ) : (
            <Typography
              id={labelId}
              variant="subtitle1"
              component="h3"
              sx={{
                fontWeight: 'var(--fw-bold)', color: inactive ? 'var(--text-secondary)' : 'var(--text-primary)',
                overflowWrap: 'anywhere', lineHeight: 1.35, pt: '5px', textWrap: 'pretty',
              }}
            >
              {q.label}
            </Typography>
          )}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            <Pill icon={<TypeIcon />} tone="primary">
              {q.typeLabel}{q.rulesLabel ? ` · ${q.rulesLabel}` : ''}
            </Pill>
            {q.targetLabel && <Pill icon={<TargetIcon />}>{q.targetLabel}</Pill>}
            {q.visibilityLabel && <Pill icon={<VisIcon />}>{q.visibilityLabel}</Pill>}
            {q.requiredLabel && <Pill icon={<Box component="span" sx={{ fontWeight: 'var(--fw-bold)', lineHeight: 1 }}>*</Box>} tone="info">{q.requiredLabel}</Pill>}
          </Box>

          <AudienceTrail label={q.audienceLabel} />

          {warnings.map((w) => (
            <Hint key={w.key} tone={w.status === 'warning' || w.status === 'error' ? w.status : 'info'}>{w.message}</Hint>
          ))}
        </Box>

        <IconButton
          {...handleProps}
          aria-label={`Mover "${q.label}" (posição ${index + 1} de ${total}). Arraste, ou use as setas para cima e para baixo.`}
          sx={{
            ...ICON_BTN_SX, cursor: 'grab', touchAction: 'none', mt: '-4px', mr: '-6px',
            '&:active': { cursor: 'grabbing' },
          }}
        >
          <DragIndicatorRounded />
        </IconButton>
      </Box>

      {confirm ? (
        <DeleteConfirm c={confirm} a={a} quiet={quiet} focusFlag={focoNaConfirmacao} />
      ) : (
        <Box
          sx={{
            mt: 1.75, pt: 1.25, borderTop: '1px solid', borderColor: 'var(--divider)',
            display: 'flex', alignItems: 'center', gap: 0.25, flexWrap: 'wrap',
          }}
        >
          <Tooltip title={toggleBlocked ? (planMessage || '') : ''} disableHoverListener={!toggleBlocked} disableFocusListener={!toggleBlocked} disableTouchListener={!toggleBlocked}>
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: { xs: 0.75, sm: 1 }, mr: 'auto', minHeight: 40, pr: { xs: 0, sm: 1 } }}>
              <Switch
                checked={!!q.isActive}
                disabled={toggleBlocked || !!renaming}
                onChange={() => a.onToggleActive?.(q.id)}
                inputProps={{ 'aria-label': `${q.toggleLabel}: ${q.label}` }}
                sx={SWITCH_SX}
              />
              <Typography variant="body2" sx={{ fontWeight: 'var(--fw-medium)', color: q.isActive ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                {q.statusLabel}
              </Typography>
              {toggleBlocked && <LockOutlined aria-hidden="true" sx={{ fontSize: 16, color: 'var(--text-secondary)', display: { xs: 'none', sm: 'inline-block' } }} />}
            </Box>
          </Tooltip>
          <Tooltip title="Subir">
            <span>
              <IconButton aria-label={`Subir "${q.label}"`} disabled={index === 0} onClick={() => onMove(q.id, -1)} sx={ICON_BTN_SX}>
                <ArrowUpwardRounded sx={{ fontSize: 20 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Descer">
            <span>
              <IconButton aria-label={`Descer "${q.label}"`} disabled={index === total - 1} onClick={() => onMove(q.id, 1)} sx={ICON_BTN_SX}>
                <ArrowDownwardRounded sx={{ fontSize: 20 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Box aria-hidden="true" sx={{ width: '1px', height: 24, bgcolor: 'var(--divider)', mx: 0.5, display: { xs: 'none', sm: 'block' } }} />
          <Tooltip title="Trocar rótulo">
            <span>
              <IconButton aria-label={`Trocar o rótulo de "${q.label}"`} disabled={!!renaming} onClick={() => a.onRename?.(q.id)} sx={ICON_BTN_SX}>
                <EditRounded sx={{ fontSize: 20 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Apagar">
            <IconButton ref={delRef} aria-label={`Apagar "${q.label}"`} onClick={() => a.onDelete?.(q.id)} sx={REMOVE_BTN_SX}>
              <DeleteOutlineRounded sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
}

/* ── Lista ordenável: arrastar pela alça, ↑/↓ no cartão, setas do teclado na alça ── */
function QuestionList({ vm, a, quiet, cardRefs, titleRef }) {
  const qs = vm.questions || [];
  const ids = qs.map((q) => q.id);
  const [drag, setDrag] = useState(null); // UI: { id, from, to, y0, dy, rects }
  const [anuncio, setAnuncio] = useState('');
  const labels = vm.labels || {};

  const announce = (id, pos) => {
    const q = qs.find((x) => x.id === id);
    setAnuncio(`"${q?.label || ''}" agora está na posição ${pos} de ${ids.length}.`);
  };
  const move = (id, delta) => {
    const i = ids.indexOf(id);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= ids.length) return;
    const next = [...ids];
    next.splice(i, 1);
    next.splice(j, 0, id);
    a.onReorder?.(next);
    announce(id, j + 1);
  };

  const onPointerDown = (e, id) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    const from = ids.indexOf(id);
    const rects = ids.map((k) => {
      const r = cardRefs.current[k]?.getBoundingClientRect();
      return r ? { top: r.top, h: r.height } : { top: 0, h: 0 };
    });
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* sem captura */ }
    setDrag({ id, from, to: from, y0: e.clientY, dy: 0, rects });
  };
  const onPointerMove = (e) => {
    if (!drag) return;
    const dy = e.clientY - drag.y0;
    const r = drag.rects;
    const center = r[drag.from].top + r[drag.from].h / 2 + dy;
    let to = 0;
    for (let i = 0; i < r.length; i += 1) {
      if (i !== drag.from && r[i].top + r[i].h / 2 < center) to += 1;
    }
    setDrag((d) => (d ? { ...d, dy, to } : d));
  };
  const onPointerUp = () => {
    if (!drag) return;
    const { from, to, id } = drag;
    setDrag(null);
    if (to !== from) {
      const next = [...ids];
      next.splice(from, 1);
      next.splice(to, 0, id);
      a.onReorder?.(next);
      announce(id, to + 1);
    }
  };

  const dragSxFor = (i, id) => {
    if (!drag) return null;
    if (id === drag.id) {
      return {
        transform: `translateY(${drag.dy}px) scale(1.012)`, zIndex: 3, transition: 'box-shadow .15s',
        boxShadow: 'var(--shadow-hover)', borderColor: 'var(--color-primary)', cursor: 'grabbing',
      };
    }
    const r = drag.rects;
    const f = drag.from;
    const gap = f + 1 < r.length ? r[f + 1].top - (r[f].top + r[f].h) : f > 0 ? r[f].top - (r[f - 1].top + r[f - 1].h) : 0;
    const shift = r[f].h + gap;
    if (f < drag.to && i > f && i <= drag.to) return { transform: `translateY(${-shift}px)` };
    if (f > drag.to && i >= drag.to && i < f) return { transform: `translateY(${shift}px)` };
    return { transform: 'none' };
  };

  // Durante o arraste, os números já mostram a ordem que vai ficar.
  const projected = drag && drag.to !== drag.from
    ? (() => { const n = [...ids]; n.splice(drag.from, 1); n.splice(drag.to, 0, drag.id); return n; })()
    : ids;

  return (
    <Box component="section" aria-labelledby="cf-perguntas" sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
        <Typography ref={titleRef} tabIndex={-1} id="cf-perguntas" variant="subtitle1" component="h2" sx={{ ...FOCUS_TITLE_SX, fontWeight: 'var(--fw-bold)' }}>
          {labels.questionsTitle}
        </Typography>
        {labels.reorderHint && qs.length > 1 && (
          <Typography variant="caption" sx={{ color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <DragIndicatorRounded aria-hidden="true" sx={{ fontSize: 16 }} />
            {labels.reorderHint}
          </Typography>
        )}
      </Box>
      <Box
        component="ol"
        sx={{
          listStyle: 'none', m: 0, p: 0, position: 'relative',
          display: 'flex', flexDirection: 'column', gap: 1.75,
          userSelect: drag ? 'none' : 'auto',
          // A "espinha" do formulário: liga os números de um cartão ao outro, nos vãos.
          '&::before': qs.length > 1 ? {
            content: '""', position: 'absolute', top: 28, bottom: 28, left: { xs: '31px', sm: '33px' },
            borderLeft: '2px dashed', borderColor: 'var(--color-primary-light)', opacity: 0.55,
          } : undefined,
        }}
      >
        {qs.map((q, i) => (
          <Box component="li" key={q.id} sx={{ position: 'relative', zIndex: drag?.id === q.id ? 3 : 1 }}>
            <QuestionCard
              q={q}
              index={i}
              shownIndex={projected.indexOf(q.id)}
              total={qs.length}
              vm={vm}
              a={a}
              quiet={quiet}
              planMessage={vm.planUsage?.message}
              cardRef={(el) => { cardRefs.current[q.id] = el; }}
              dragSx={dragSxFor(i, q.id)}
              onMove={move}
              handleProps={{
                onPointerDown: (e) => onPointerDown(e, q.id),
                onPointerMove,
                onPointerUp,
                onPointerCancel: () => setDrag(null),
                onKeyDown: (e) => {
                  if (e.key === 'ArrowUp') { e.preventDefault(); move(q.id, -1); }
                  if (e.key === 'ArrowDown') { e.preventDefault(); move(q.id, 1); }
                },
              }}
            />
          </Box>
        ))}
      </Box>
      <Box role="status" aria-live="polite" sx={SR_ONLY_SX}>{anuncio}</Box>
    </Box>
  );
}

/* ── Dados básicos: "ligar um recurso" ────────────────────────────────── */
function BasicDataPanel({ vm, a }) {
  const labels = vm.labels || {};
  const rows = vm.basicData || [];
  if (!rows.length) return null;
  return (
    <Box component="section" aria-labelledby="cf-basicos" sx={{ ...CARD_SX, p: { xs: 2, sm: 2.25 }, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
      <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
        <IconDot icon={<ToggleOnRounded sx={{ fontSize: 22 }} />} tone="primary" size={36} />
        <Box sx={{ minWidth: 0 }}>
          <Typography id="cf-basicos" variant="subtitle1" component="h2" sx={{ fontWeight: 'var(--fw-bold)' }}>{labels.basicDataTitle}</Typography>
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mt: 0.25, lineHeight: 1.45 }}>{labels.basicDataHint}</Typography>
        </Box>
      </Box>
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {rows.map((r) => {
          const Icon = BASIC_ICON[r.systemKey] || BadgeRounded;
          const on = !!r.isActive;
          const switchId = `basico-${r.systemKey}`;
          return (
            <Box
              component="li"
              key={r.systemKey}
              sx={{
                display: 'flex', flexDirection: 'column', gap: 1, p: 1.25,
                borderRadius: 'var(--radius-sm)', border: '1px solid',
                borderColor: on ? 'var(--color-primary)' : 'var(--divider)',
                bgcolor: on ? 'var(--color-primary-soft)' : 'var(--surface)',
                transition: 'background-color .2s, border-color .2s',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <IconDot
                  key={on ? 'on' : 'off'}
                  icon={<Icon sx={{ fontSize: 20 }} />}
                  tone={on ? 'solid' : 'neutral'}
                  size={38}
                  sx={on ? { animation: `${pop} .45s ease-out`, [NO_MOTION]: { animation: 'none' } } : undefined}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography component="label" htmlFor={switchId} variant="subtitle2" sx={{ display: 'block', color: on ? 'var(--color-primary-dark)' : 'var(--text-primary)', cursor: 'pointer' }}>
                    {r.label}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'var(--text-secondary)', lineHeight: 1.4, mt: '2px' }}>{r.description}</Typography>
                </Box>
                <Tooltip title={r.toggleLabel}>
                  <Switch
                    id={switchId}
                    checked={on}
                    onChange={() => (on ? a.onStopBasic?.(r.fieldId) : a.onUseBasic?.(r.systemKey))}
                    inputProps={{ 'aria-label': `${r.toggleLabel}: ${r.label}` }}
                    sx={SWITCH_SX}
                  />
                </Tooltip>
              </Box>
              {on && (r.audienceLabel || r.requiredLabel) && (
                <Box sx={{ pl: { xs: 0, sm: 6.25 }, display: 'flex', flexDirection: 'column', gap: 0.75, animation: `${rise} .3s ease-out`, [NO_MOTION]: { animation: 'none' } }}>
                  <AudienceTrail label={r.audienceLabel} />
                  {r.requiredLabel && (
                    <Box><Pill icon={<Box component="span" sx={{ fontWeight: 'var(--fw-bold)', lineHeight: 1 }}>*</Box>} tone="info">{r.requiredLabel}</Pill></Box>
                  )}
                </Box>
              )}
              {(r.warnings || []).map((w) => (
                <Hint key={w.key} tone={w.status === 'warning' || w.status === 'error' ? w.status : 'info'}>{w.message}</Hint>
              ))}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

/* ── Perguntas prontas do nicho ───────────────────────────────────────── */
function SuggestionsPanel({ vm, a, featured }) {
  const list = vm.suggestions || [];
  if (!list.length) return null;
  return (
    <Box
      component="section"
      aria-labelledby="cf-sugestoes"
      sx={{
        ...CARD_SX, p: { xs: 2, sm: 2.25 }, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0,
        ...(featured && { borderColor: 'var(--color-primary-light)', bgcolor: 'var(--surface)' }),
      }}
    >
      <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
        <IconDot icon={<AutoAwesomeRounded sx={{ fontSize: 20 }} />} tone={featured ? 'solid' : 'primary'} size={36} />
        <Box sx={{ minWidth: 0 }}>
          <Typography id="cf-sugestoes" variant="subtitle1" component="h2" sx={{ fontWeight: 'var(--fw-bold)' }}>
            {featured ? 'Prontas para o seu negócio' : 'Perguntas prontas'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mt: 0.25, lineHeight: 1.45 }}>
            Toque para abrir já preenchida. Nada é criado antes de você revisar.
          </Typography>
        </Box>
      </Box>
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {list.map((s, i) => {
          const Icon = typeIcon(s.typeLabel);
          return (
            <Box component="li" key={s.key}>
              <ButtonBase
                onClick={() => a.onUseSuggestion?.(s.key)}
                aria-label={`Usar a pergunta pronta "${s.label}" (${s.typeLabel})`}
                sx={{
                  ...FOCUS_SX, width: '100%', justifyContent: 'flex-start', textAlign: 'left',
                  display: 'flex', alignItems: 'center', gap: 1.25, p: 1.25, minHeight: 56,
                  borderRadius: 'var(--radius-sm)', border: '1px solid', borderColor: 'var(--divider)', bgcolor: 'var(--surface)',
                  transition: 'border-color .15s, background-color .15s, transform .15s',
                  animation: featured ? `${rise} .45s ${i * 80}ms both` : 'none',
                  [NO_MOTION]: { animation: 'none' },
                  '&:hover': { borderColor: 'var(--color-primary)', bgcolor: 'var(--color-primary-soft)' },
                  '&:hover .sugAdd': { bgcolor: 'var(--color-primary)', color: 'var(--on-primary)' },
                }}
              >
                <IconDot icon={<Icon sx={{ fontSize: 18 }} />} tone="neutral" size={34} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant={featured ? 'subtitle1' : 'subtitle2'} sx={{ color: 'var(--text-primary)', overflowWrap: 'anywhere', lineHeight: 1.35, fontWeight: 'var(--fw-bold)' }}>
                    {s.label}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'var(--text-secondary)', mt: '2px' }}>{s.typeLabel}</Typography>
                </Box>
                <Box
                  className="sugAdd"
                  aria-hidden="true"
                  sx={{
                    width: 32, height: 32, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: 'var(--color-primary-soft)', color: 'var(--color-primary-dark)', transition: 'background-color .15s, color .15s',
                  }}
                >
                  <AddRounded sx={{ fontSize: 20 }} />
                </Box>
              </ButtonBase>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

/* ── Vazio: o formulário-esboço com os exemplos ───────────────────────── */
function FormSketch({ examples, onPick }) {
  return (
    <Box sx={{ width: '100%', maxWidth: 420, mx: 'auto' }}>
      <Typography variant="caption" sx={{ color: 'var(--text-secondary)', fontWeight: 'var(--fw-bold)', mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <TouchAppRounded aria-hidden="true" sx={{ fontSize: 16 }} />
        Toque num exemplo para começar
      </Typography>
      <Box
        sx={{
          bgcolor: 'var(--surface)', border: '1px solid', borderColor: 'var(--divider)', borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-hover)', p: { xs: 1.5, sm: 2 }, display: 'flex', flexDirection: 'column', gap: 0.5,
        }}
      >
        <Box aria-hidden="true" sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.25, pt: 0.5, pb: 1.25, mb: 0.5, borderBottom: '1px solid', borderColor: 'var(--divider)' }}>
          <Box sx={{ width: 30, height: 30, borderRadius: '50%', bgcolor: 'var(--color-primary-soft)' }} />
          <Box sx={{ flex: 1 }}>
            <Box sx={{ height: 8, width: '55%', borderRadius: 999, bgcolor: 'var(--divider)', mb: 0.75 }} />
            <Box sx={{ height: 6, width: '35%', borderRadius: 999, bgcolor: 'var(--divider)' }} />
          </Box>
        </Box>
        {(examples || []).map((ex, i) => (
          <ButtonBase
            key={ex}
            onClick={() => onPick(ex)}
            aria-label={`Começar com o exemplo "${ex}"`}
            sx={{
              ...FOCUS_SX, display: 'flex', flexDirection: 'column', alignItems: 'stretch', textAlign: 'left', gap: 0.75,
              p: 1.25, borderRadius: 'var(--radius-sm)', border: '1px solid transparent',
              animation: `${rise} .5s ${120 + i * 90}ms both`, [NO_MOTION]: { animation: 'none' },
              transition: 'background-color .15s, border-color .15s',
              '&:hover': { bgcolor: 'var(--color-primary-soft)', borderColor: 'var(--color-primary-soft)' },
              '&:hover .ghost': { borderColor: 'var(--color-primary)', color: 'var(--color-primary-dark)' },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 'var(--fw-bold)', color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>{ex}</Typography>
            <Box
              className="ghost"
              aria-hidden="true"
              sx={{
                height: 36, borderRadius: 'var(--radius-sm)', border: '1px dashed', borderColor: 'var(--text-disabled)',
                display: 'flex', alignItems: 'center', justifyContent: 'flex-end', px: 1, color: 'var(--text-secondary)',
                transition: 'border-color .15s, color .15s',
              }}
            >
              <AddRounded sx={{ fontSize: 18 }} />
            </Box>
          </ButtonBase>
        ))}
      </Box>
    </Box>
  );
}

function EmptyHero({ vm, a, newButtonIcon }) {
  const e = vm.emptyState || {};
  return (
    <Box sx={{ ...CARD_SX, overflow: 'hidden', containerType: 'inline-size', containerName: 'heroi' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', '@container heroi (min-width: 720px)': { gridTemplateColumns: 'minmax(0, 1.05fr) minmax(0, 1fr)' } }}>
        <Box sx={{ p: { xs: 2.5, sm: 4 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: 1.5 }}>
          <Pill icon={<AutoAwesomeRounded />} tone="primary">Novo por aqui</Pill>
          <Typography variant="h2" component="h2" sx={{ color: 'var(--text-primary)', textWrap: 'balance' }}>{e.title}</Typography>
          <Typography variant="body1" sx={{ color: 'var(--text-secondary)', maxWidth: 480, lineHeight: 1.55, textWrap: 'pretty' }}>{e.message}</Typography>
          <Button
            onClick={() => a.onNew?.()}
            startIcon={newButtonIcon}
            disableElevation
            sx={{ ...PRIMARY_BTN_SX, mt: 1, minHeight: 46, px: 2.75, width: { xs: '100%', sm: 'auto' } }}
          >
            {e.actionLabel}
          </Button>
        </Box>
        <Box
          sx={{
            bgcolor: 'var(--bg-page)', p: { xs: 2.5, sm: 4 }, display: 'flex', alignItems: 'center',
            borderTop: '1px solid', borderColor: 'var(--divider)',
            '@container heroi (min-width: 720px)': { borderTop: 0, borderLeft: '1px solid', borderColor: 'var(--divider)' },
          }}
        >
          <FormSketch examples={e.examples} onPick={(ex) => a.onUseExample?.(ex)} />
        </Box>
      </Box>
    </Box>
  );
}

/* ── Carregando ───────────────────────────────────────────────────────── */
function LoadingScreen() {
  const card = (k, w) => (
    <Box key={k} sx={{ ...CARD_SX, p: 2.25, display: 'grid', gridTemplateColumns: '32px minmax(0, 1fr)', columnGap: 1.5 }}>
      <Skeleton variant="circular" animation="wave" width={32} height={32} sx={BONE} />
      <Box>
        <Skeleton variant="rounded" animation="wave" height={16} width={w} sx={{ ...BONE, mb: 1.25, mt: 0.75 }} />
        <Box sx={{ display: 'flex', gap: 0.75, mb: 1.25, flexWrap: 'wrap' }}>
          {[110, 150, 96].map((x) => <Skeleton key={x} variant="rounded" animation="wave" height={22} width={x} sx={{ ...BONE, borderRadius: 999 }} />)}
        </Box>
        <Skeleton variant="rounded" animation="wave" height={22} width="70%" sx={{ ...BONE, borderRadius: 999, mb: 1.75 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 1.25, borderTop: '1px solid', borderColor: 'var(--divider)' }}>
          <Skeleton variant="rounded" animation="wave" width={44} height={26} sx={{ ...BONE, borderRadius: 999 }} />
          <Box sx={{ flex: 1 }} />
          {[0, 1, 2].map((x) => <Skeleton key={x} variant="circular" animation="wave" width={32} height={32} sx={BONE} />)}
        </Box>
      </Box>
    </Box>
  );
  const row = (k) => (
    <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: 1.25, border: '1px solid', borderColor: 'var(--divider)', borderRadius: 'var(--radius-sm)' }}>
      <Skeleton variant="circular" animation="wave" width={38} height={38} sx={BONE} />
      <Box sx={{ flex: 1 }}>
        <Skeleton variant="rounded" animation="wave" height={12} width="50%" sx={{ ...BONE, mb: 0.75 }} />
        <Skeleton variant="rounded" animation="wave" height={10} width="75%" sx={BONE} />
      </Box>
      <Skeleton variant="rounded" animation="wave" width={44} height={26} sx={{ ...BONE, borderRadius: 999 }} />
    </Box>
  );
  return (
    <Box role="status" aria-live="polite" aria-busy="true">
      <Box component="span" sx={SR_ONLY_SX}>Carregando perguntas…</Box>
      <Box aria-hidden="true">
        <Box sx={{ ...CARD_SX, p: 2, mb: 'var(--space-3)', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Skeleton variant="rounded" animation="wave" height={30} width={170} sx={{ ...BONE, borderRadius: 999 }} />
          <Skeleton variant="rounded" animation="wave" height={30} width={120} sx={{ ...BONE, borderRadius: 999 }} />
        </Box>
        <Box sx={LAYOUT_SX}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
            <Skeleton variant="rounded" animation="wave" height={16} width={140} sx={{ ...BONE, mb: 0.5 }} />
            {card('a', '62%')}
            {card('b', '48%')}
            {card('c', '70%')}
          </Box>
          <Box sx={{ ...CARD_SX, p: 2.25, display: 'flex', flexDirection: 'column', gap: 1, alignSelf: 'start' }}>
            <Skeleton variant="rounded" animation="wave" height={16} width="45%" sx={{ ...BONE, mb: 0.5 }} />
            <Skeleton variant="rounded" animation="wave" height={10} width="80%" sx={{ ...BONE, mb: 1 }} />
            {['1', '2', '3', '4'].map(row)}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// Em largura de verdade a tela reflui em colunas: perguntas à esquerda, recursos e sugestões à direita.
const LAYOUT_SX = {
  display: 'grid', gap: 'var(--space-3)', gridTemplateColumns: 'minmax(0, 1fr)', alignItems: 'start',
  '@container perguntas (min-width: 980px)': { gridTemplateColumns: 'minmax(0, 1.7fr) minmax(320px, 1fr)' },
  '@container perguntas (min-width: 1400px)': { gridTemplateColumns: 'minmax(0, 2fr) minmax(380px, 1fr)' },
};
// Coluna lateral: duas lado a lado no tablet, empilhadas quando vira a coluna da direita.
const ASIDE_SX = {
  display: 'grid', gap: 'var(--space-2)', gridTemplateColumns: 'minmax(0, 1fr)', alignItems: 'start', minWidth: 0,
  '@container perguntas (min-width: 640px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
  '@container perguntas (min-width: 980px)': { gridTemplateColumns: 'minmax(0, 1fr)' },
};

/* ════════════════════════════════════════════════════════════════════════
   CRIAÇÃO — passos à esquerda, prévia ao vivo à direita
   ════════════════════════════════════════════════════════════════════════ */
function CreatorSection({ n, id, title, hint, children, sx }) {
  return (
    <Box
      component="section"
      aria-labelledby={id}
      sx={{
        ...CARD_SX, p: { xs: 1.75, sm: 2.5 }, minWidth: 0,
        display: 'grid', gridTemplateColumns: { xs: '28px minmax(0, 1fr)', sm: '32px minmax(0, 1fr)' }, columnGap: { xs: 1.25, sm: 1.5 },
        ...sx,
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 }, borderRadius: '50%',
          bgcolor: 'var(--color-primary-soft)', color: 'var(--color-primary-dark)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Typography component="span" variant="subtitle2" sx={{ color: 'inherit' }}>{n}</Typography>
      </Box>
      <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ pt: { xs: '3px', sm: '5px' } }}>
          <Typography id={id} variant="subtitle1" component="h3" sx={{ fontWeight: 'var(--fw-bold)', color: 'var(--text-primary)' }}>{title}</Typography>
          {hint && <Typography variant="caption" sx={{ color: 'var(--text-secondary)', mt: 0.5, lineHeight: 1.45 }}>{hint}</Typography>}
        </Box>
        {children}
      </Box>
    </Box>
  );
}

// Tipo de resposta: grupos como abas, tipos como peças grandes (bom para o polegar).
function TypePicker({ groups, onPick, disabled }) {
  const chosenGroup = groups.find((g) => (g.options || []).some((o) => o.isSelected))?.key;
  const [tab, setTab] = useState(chosenGroup || groups[0]?.key);
  const cur = groups.find((g) => g.key === tab) || groups[0];
  if (!cur) return null;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, minWidth: 0 }}>
      <Box role="tablist" aria-label="Grupos de tipo de resposta" sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
        {groups.map((g) => {
          const on = g.key === cur.key;
          const has = g.key === chosenGroup;
          const Icon = GROUP_ICON[g.key] || TuneRounded;
          return (
            <ButtonBase
              key={g.key}
              role="tab"
              id={`tp-aba-${g.key}`}
              aria-selected={on}
              aria-controls="tp-painel"
              onClick={() => setTab(g.key)}
              sx={{
                ...FOCUS_SX, minHeight: 40, px: 1.5, gap: 0.75, borderRadius: 999,
                border: '1px solid', borderColor: on ? 'var(--text-primary)' : 'var(--divider)',
                bgcolor: on ? 'var(--text-primary)' : 'var(--surface)', color: on ? 'var(--surface)' : 'var(--text-primary)',
                transition: 'background-color .15s, color .15s, border-color .15s',
                '&:hover': on ? {} : { borderColor: 'var(--color-primary)' },
              }}
            >
              <Icon aria-hidden="true" sx={{ fontSize: 18 }} />
              <Typography component="span" variant="body2" sx={{ color: 'inherit', fontWeight: 'var(--fw-medium)' }}>{g.label}</Typography>
              {has && (
                <>
                  <Box aria-hidden="true" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: on ? 'var(--color-primary-light)' : 'var(--color-primary)' }} />
                  <Box component="span" sx={SR_ONLY_SX}>(tipo escolhido)</Box>
                </>
              )}
            </ButtonBase>
          );
        })}
      </Box>
      <Box
        id="tp-painel"
        role="radiogroup"
        aria-labelledby={`tp-aba-${cur.key}`}
        sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 128px), 1fr))' }}
      >
        {(cur.options || []).map((o, i) => {
          const on = !!o.isSelected;
          const Icon = TYPE_ICON_BY_VALUE[o.value] || HelpOutlineRounded;
          return (
            <ButtonBase
              key={o.value}
              role="radio"
              aria-checked={on}
              disabled={disabled}
              onClick={() => onPick(o.value)}
              sx={{
                ...FOCUS_SX, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                textAlign: 'left', gap: 0.75, p: 1.5, minHeight: 96, borderRadius: 'var(--radius-sm)',
                border: '1px solid', borderColor: on ? 'var(--color-primary)' : 'var(--divider)',
                bgcolor: on ? 'var(--color-primary-soft)' : 'var(--surface)',
                boxShadow: on ? 'inset 0 0 0 1px var(--color-primary)' : 'none',
                transition: 'background-color .15s, border-color .15s, box-shadow .15s',
                animation: `${rise} .3s ${i * 40}ms both`, [NO_MOTION]: { animation: 'none' },
                '&:hover': { borderColor: 'var(--color-primary)' },
              }}
            >
              <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
                <IconDot icon={<Icon sx={{ fontSize: 18 }} />} tone={on ? 'solid' : 'neutral'} size={30} />
                {on && <CheckRounded aria-hidden="true" sx={{ fontSize: 20, color: 'var(--color-primary-dark)' }} />}
              </Box>
              <Typography component="span" variant="subtitle2" sx={{ color: on ? 'var(--color-primary-dark)' : 'var(--text-primary)' }}>{o.label}</Typography>
              <Typography component="span" variant="caption" sx={{ color: 'var(--text-secondary)', lineHeight: 1.4 }}>{o.description}</Typography>
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
}

// Opções da escolha: lista editável (dezenas cabem), adicionar com Enter, subir e remover.
function OptionsEditor({ rule, onChange, labelId, disabled }) {
  const list = Array.isArray(rule.value) ? rule.value : [];
  const [draft, setDraft] = useState(''); // UI: rascunho da próxima opção
  const set = (next) => onChange(rule.key, next);
  const add = () => {
    const t = draft.trim();
    if (!t) return;
    set([...list, { id: '', label: t }]);
    setDraft('');
  };
  const addId = `${labelId}-nova`;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Box component="ol" aria-labelledby={labelId} sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {list.map((o, i) => (
          <Box component="li" key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box aria-hidden="true" sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: 'var(--bg-page)', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mr: 0.25 }}>
              <Typography component="span" variant="caption" sx={{ color: 'inherit', fontWeight: 'var(--fw-bold)' }}>{i + 1}</Typography>
            </Box>
            <TextField
              fullWidth
              disabled={disabled}
              value={o.label ?? ''}
              onChange={(e) => set(list.map((x, k) => (k === i ? { ...x, label: e.target.value } : x)))}
              inputProps={{ 'aria-label': `Opção ${i + 1}` }}
              sx={FIELD_SX}
            />
            <IconButton
              aria-label={`Subir a opção ${i + 1}`}
              disabled={disabled || i === 0}
              onClick={() => { const n = [...list]; [n[i - 1], n[i]] = [n[i], n[i - 1]]; set(n); }}
              sx={ICON_BTN_SX}
            >
              <ArrowUpwardRounded sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton aria-label={`Remover a opção ${i + 1}`} disabled={disabled} onClick={() => set(list.filter((_, k) => k !== i))} sx={REMOVE_BTN_SX}>
              <CloseRounded sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        ))}
      </Box>
      <Box sx={{ mt: 0.5 }}>
        <Typography component="label" htmlFor={addId} variant="caption" sx={{ color: 'var(--text-secondary)', fontWeight: 'var(--fw-medium)', mb: 0.5 }}>
          Nova opção
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
          <TextField
            id={addId}
            fullWidth
            disabled={disabled}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
            sx={FIELD_SX}
          />
          <Button onClick={add} disabled={disabled || !draft.trim()} startIcon={<AddRounded />} sx={{ ...OUTLINE_BTN_SX, px: 1.75, flexShrink: 0 }}>
            Adicionar
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

function RuleInput({ rule, onChange, disabled }) {
  const id = `regra-${rule.key}`;
  const labelId = `${id}-rotulo`;
  const descId = `${id}-ajuda`;
  const set = (v) => onChange(rule.key, v);
  const common = { id, fullWidth: true, disabled, error: !!rule.error, sx: FIELD_SX };
  let control;
  let group = false;
  if (rule.kind === 'options') {
    group = true;
    control = <OptionsEditor rule={rule} onChange={onChange} labelId={labelId} disabled={disabled} />;
  } else if (rule.kind === 'select') {
    const choices = rule.choices || [];
    if (choices.length <= 4) {
      group = true;
      control = (
        <ToggleButtonGroup
          exclusive
          value={rule.value}
          onChange={(e, v) => { if (v !== null) set(v); }}
          aria-labelledby={labelId}
          disabled={disabled}
          sx={{ ...SEGMENT_SX, flexWrap: 'wrap' }}
        >
          {choices.map((c) => (
            <ToggleButton key={c.value} value={c.value}>
              {rule.value === c.value && <CheckRounded aria-hidden="true" sx={{ fontSize: 16 }} />}
              {c.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      );
    } else {
      control = (
        <TextField {...common} select value={rule.value ?? ''} onChange={(e) => set(e.target.value)} inputProps={{ 'aria-describedby': descId }}>
          {choices.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
        </TextField>
      );
    }
  } else if (rule.kind === 'text') {
    control = (
      <TextField {...common} multiline maxRows={6} value={rule.value ?? ''} onChange={(e) => set(e.target.value)} inputProps={{ 'aria-describedby': descId }} />
    );
  } else {
    const type = rule.kind === 'date' || rule.kind === 'time' ? rule.kind : 'text';
    control = (
      <TextField
        {...common}
        type={type}
        value={rule.value ?? ''}
        onChange={(e) => set(e.target.value)}
        inputProps={{ 'aria-describedby': descId, inputMode: rule.kind === 'number' ? 'decimal' : undefined }}
      />
    );
  }
  return (
    <FieldShell id={id} labelId={labelId} descId={descId} label={rule.label} group={group} helpText={rule.hint} error={rule.error}>
      {control}
    </FieldShell>
  );
}

function ChoiceCards({ field, onPick, disabled, idBase }) {
  if (!field) return null;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box
        role="radiogroup"
        aria-labelledby={idBase}
        sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 210px), 1fr))' }}
      >
        {(field.options || []).map((o) => {
          const on = o.value === field.value;
          const Mark = on ? RadioButtonCheckedRounded : RadioButtonUncheckedRounded;
          return (
            <ButtonBase
              key={o.value}
              role="radio"
              aria-checked={on}
              disabled={disabled}
              onClick={() => onPick(o.value)}
              sx={{
                ...FOCUS_SX, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', gap: 1.25, textAlign: 'left',
                p: 1.5, minHeight: 64, borderRadius: 'var(--radius-sm)',
                border: '1px solid', borderColor: on ? 'var(--color-primary)' : 'var(--divider)',
                bgcolor: on ? 'var(--color-primary-soft)' : 'var(--surface)',
                boxShadow: on ? 'inset 0 0 0 1px var(--color-primary)' : 'none',
                transition: 'background-color .15s, border-color .15s, box-shadow .15s',
                '&:hover': { borderColor: 'var(--color-primary)' },
              }}
            >
              <Mark aria-hidden="true" sx={{ fontSize: 20, flexShrink: 0, mt: '1px', color: on ? 'var(--color-primary-dark)' : 'var(--text-secondary)' }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography component="span" variant="subtitle2" sx={{ display: 'block', color: on ? 'var(--color-primary-dark)' : 'var(--text-primary)' }}>{o.label}</Typography>
                <Typography component="span" variant="caption" sx={{ color: 'var(--text-secondary)', mt: 0.25, lineHeight: 1.4 }}>{o.description}</Typography>
              </Box>
            </ButtonBase>
          );
        })}
      </Box>
      {field.hint && (
        <Typography variant="caption" sx={{ color: 'var(--text-secondary)', display: 'flex', gap: 0.5, alignItems: 'flex-start', lineHeight: 1.4 }}>
          <InfoOutlined aria-hidden="true" sx={{ fontSize: 15, mt: '1px', flexShrink: 0 }} />
          {field.hint}
        </Typography>
      )}
    </Box>
  );
}

function FilterBlock({ f, a, disabled }) {
  const Icon = FILTER_ICON[f.key] || TuneRounded;
  const labelId = `filtro-${f.key}`;
  return (
    <Box
      sx={{
        border: '1px solid', borderColor: f.warning ? 'var(--tint-warning-fg)' : 'var(--divider)', borderRadius: 'var(--radius-sm)',
        p: 1.25, display: 'flex', flexDirection: 'column', gap: 1.25, bgcolor: 'var(--surface)', minWidth: 0,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <IconDot icon={<Icon sx={{ fontSize: 18 }} />} tone="primary" size={32} />
        <Box sx={{ flex: '1 1 90px', minWidth: 0 }}>
          <Typography id={labelId} variant="subtitle2" sx={{ color: 'var(--text-primary)' }}>{f.label}</Typography>
          {f.mode === 'selected' && (
            <Typography variant="caption" sx={{ color: f.warning ? 'var(--tint-warning-fg)' : 'var(--text-secondary)', fontWeight: 'var(--fw-medium)' }}>{f.summaryLabel}</Typography>
          )}
        </Box>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={f.mode}
          disabled={disabled}
          onChange={(e, v) => { if (v) a.onFilterModeChange?.(f.key, v); }}
          aria-labelledby={labelId}
          sx={SEGMENT_SX}
        >
          <ToggleButton value="all">{f.mode === 'all' && <CheckRounded aria-hidden="true" sx={{ fontSize: 16 }} />}Todos</ToggleButton>
          <ToggleButton value="selected">{f.mode === 'selected' && <CheckRounded aria-hidden="true" sx={{ fontSize: 16 }} />}Escolher</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {f.mode === 'selected' && (
        <Box sx={{ animation: `${rise} .25s ease-out`, [NO_MOTION]: { animation: 'none' } }}>
          <ChoicePills
            multi
            opts={(f.options || []).map((o) => ({ value: o.id, label: o.label }))}
            isOn={(id) => (f.options || []).find((o) => o.id === id)?.isSelected}
            onToggle={(id) => a.onFilterToggle?.(f.key, id)}
            labelId={labelId}
            disabled={disabled}
          />
        </Box>
      )}
      {f.warning && <Hint tone="warning">{f.warning}</Hint>}
    </Box>
  );
}

// A prévia: o campo como quem agenda vê, e dá para brincar nele (o valor é só da prévia).
function PreviewField({ field }) {
  const [val, setVal] = useState(null); // UI: valor brincado na prévia
  const f = { ...field, id: 'previa', value: val, label: field.label || 'Sua pergunta aparece aqui' };
  return <FieldInput field={f} onChange={(id, v) => setVal(v)} idPrefix="cf" placeholderLabel={!field.label} />;
}

function CreatorPreview({ c, compact }) {
  const vis = (c.visibility?.options || []).find((o) => o.value === c.visibility?.value);
  const tgt = (c.target?.options || []).find((o) => o.value === c.target?.value);
  const p = c.preview || {};
  return (
    <Box
      component="section"
      aria-label="Prévia"
      sx={{ ...CARD_SX, p: { xs: 1.75, sm: 2.25 }, display: 'flex', flexDirection: 'column', gap: 1.75, minWidth: 0 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box aria-hidden="true" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'var(--color-primary)', animation: `${pulse} 1.8s ease-in-out infinite`, [NO_MOTION]: { animation: 'none' } }} />
        <Typography variant="overline" sx={{ color: 'var(--text-secondary)' }}>Prévia ao vivo</Typography>
      </Box>
      <Box sx={{ bgcolor: 'var(--bg-page)', borderRadius: 'var(--radius-sm)', p: { xs: 1.25, sm: 1.75 }, border: '1px solid', borderColor: 'var(--divider)' }}>
        <Box sx={{ bgcolor: 'var(--surface)', borderRadius: 'var(--radius-sm)', p: { xs: 1.5, sm: 2 }, boxShadow: 'var(--shadow-card)' }}>
          <PreviewField key={`${p.inputKind}-${(p.options || []).length}`} field={p} />
        </Box>
      </Box>
      {(tgt || vis) && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {tgt && <Pill icon={<FolderSharedRounded />}>{tgt.label}</Pill>}
          {vis && <Pill icon={<VisibilityRounded />}>{vis.label}</Pill>}
        </Box>
      )}
      {!compact && (
        <Box sx={{ borderTop: '1px solid', borderColor: 'var(--divider)', pt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <AudienceTrail key={c.audienceLabel} label={c.audienceLabel} live big />
          {c.anyProfessionalHint && <Hint tone="info">{c.anyProfessionalHint}</Hint>}
        </Box>
      )}
    </Box>
  );
}

function CreatorDialog({ c, a }) {
  const full = useMediaQuery('(max-width:899.95px)', { noSsr: true });
  const busy = !!c.saving;
  const set = (patch) => a.onCreatorChange?.(patch);
  const isQuestion = c.mode !== 'basic';
  const titleId = 'cf-criacao-titulo';
  const labelLen = (c.label || '').length;

  // Os passos são numerados na ordem em que aparecem (variam por modo e por tipo).
  let n = 0;
  const next = () => { n += 1; return n; };

  const stepLabel = (
    <CreatorSection n={next()} id="cs-pergunta" title={isQuestion ? 'O que você quer perguntar?' : 'Como a pergunta aparece'}>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'baseline' }}>
          <Typography component="label" htmlFor="cf-rotulo" variant="caption" sx={{ color: 'var(--text-secondary)', fontWeight: 'var(--fw-bold)', mb: 0.5 }}>
            Rótulo
          </Typography>
          <Typography variant="caption" aria-hidden="true" sx={{ color: labelLen > 190 ? 'var(--tint-warning-fg)' : 'var(--text-disabled)' }}>{labelLen}/200</Typography>
        </Box>
        <TextField
          id="cf-rotulo"
          autoFocus={!full}
          fullWidth
          multiline
          maxRows={4}
          disabled={busy}
          value={c.label ?? ''}
          error={!!c.labelError}
          onChange={(e) => set({ label: e.target.value })}
          inputProps={{ maxLength: 200, 'aria-describedby': 'cf-rotulo-erro' }}
          sx={{ ...FIELD_SX, '& .MuiInputBase-input': { fontWeight: 'var(--fw-medium)' } }}
        />
        <Box id="cf-rotulo-erro">
          {c.labelError && (
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75 }}>
              <ErrorRounded aria-hidden="true" sx={{ fontSize: 16, color: 'var(--color-error)', mt: '1px' }} />
              <Typography variant="caption" sx={{ color: 'var(--color-error)', fontWeight: 'var(--fw-medium)' }}>{c.labelError}</Typography>
            </Box>
          )}
        </Box>
      </Box>
      <Box>
        <Typography component="label" htmlFor="cf-ajuda" variant="caption" sx={{ color: 'var(--text-secondary)', fontWeight: 'var(--fw-bold)', mb: 0.5 }}>
          Texto de ajuda (opcional)
        </Typography>
        <TextField
          id="cf-ajuda"
          fullWidth
          multiline
          maxRows={4}
          disabled={busy}
          value={c.helpText ?? ''}
          onChange={(e) => set({ helpText: e.target.value })}
          sx={FIELD_SX}
        />
      </Box>
    </CreatorSection>
  );

  const stepType = isQuestion && (c.typeGroups || []).length > 0 ? (
    <CreatorSection n={next()} id="cs-tipo" title="Tipo de resposta" hint="Muda o campo que a pessoa preenche. Veja na prévia.">
      <TypePicker groups={c.typeGroups} onPick={(v) => set({ fieldType: v })} disabled={busy} />
    </CreatorSection>
  ) : null;

  const rules = c.rules || [];
  const stepRules = isQuestion && rules.length > 0 ? (
    <CreatorSection n={next()} id="cs-regras" title="Regras da resposta">
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 180px), 1fr))' }}>
        {rules.map((r) => (
          <Box key={r.key} sx={{ minWidth: 0, gridColumn: r.kind === 'options' || r.kind === 'text' || (r.kind === 'select' && (r.choices || []).length > 2) ? '1 / -1' : 'auto' }}>
            <RuleInput rule={r} onChange={(k, v) => a.onRuleChange?.(k, v)} disabled={busy} />
          </Box>
        ))}
      </Box>
    </CreatorSection>
  ) : null;

  const stepTarget = c.target ? (
    <CreatorSection n={next()} id="cs-destino" title={c.target.label}>
      <ChoiceCards field={c.target} idBase="cs-destino" onPick={(v) => set({ target: v })} disabled={busy} />
    </CreatorSection>
  ) : null;

  const stepVisibility = c.visibility ? (
    <CreatorSection n={next()} id="cs-quem" title={c.visibility.label}>
      <ChoiceCards field={c.visibility} idBase="cs-quem" onPick={(v) => set({ visibility: v })} disabled={busy} />
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: 'space-between', mt: 0.5, pt: 1.5,
          borderTop: '1px solid', borderColor: 'var(--divider)',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography component="label" htmlFor="cf-obrigatoria" variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: c.requiredLocked ? 'var(--text-secondary)' : 'var(--text-primary)' }}>
            {c.requiredLocked && <LockOutlined aria-hidden="true" sx={{ fontSize: 16 }} />}
            Obrigatória na sua página
          </Typography>
          <Typography variant="caption" sx={{ color: 'var(--text-secondary)', lineHeight: 1.4, mt: '2px' }}>
            {c.requiredLocked ? 'Pergunta que só a equipe vê não pode ser obrigatória.' : c.requiredHint}
          </Typography>
        </Box>
        <Switch
          id="cf-obrigatoria"
          checked={!!c.isRequired && !c.requiredLocked}
          disabled={busy || !!c.requiredLocked}
          onChange={(e) => set({ isRequired: e.target.checked })}
          sx={SWITCH_SX}
        />
      </Box>
    </CreatorSection>
  ) : null;

  const stepFilters = (c.filters || []).length > 0 ? (
    <CreatorSection n={next()} id="cs-publico" title="Para quem aparece" hint="Comece por todos e recorte só se precisar.">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {c.filters.map((f) => <FilterBlock key={f.key} f={f} a={a} disabled={busy} />)}
      </Box>
      {/* No celular a prévia fica lá em cima: a frase também aparece aqui, ao lado das escolhas. */}
      <Box
        sx={{
          display: 'flex', flexDirection: 'column', gap: 1, p: 1.5, borderRadius: 'var(--radius-sm)', bgcolor: 'var(--bg-page)',
          '@container criacao (min-width: 860px)': { display: 'none' },
        }}
      >
        <AudienceTrail key={c.audienceLabel} label={c.audienceLabel} live big />
        {c.anyProfessionalHint && <Hint tone="info">{c.anyProfessionalHint}</Hint>}
      </Box>
    </CreatorSection>
  ) : null;

  return (
    <Dialog
      open
      onClose={() => { if (!busy) a.onCloseCreator?.(); }}
      fullScreen={full}
      maxWidth={false}
      aria-labelledby={titleId}
      PaperProps={{
        sx: {
          width: full ? '100%' : 'min(1180px, calc(100% - 64px))',
          height: full ? '100%' : 'min(900px, calc(100% - 64px))',
          maxHeight: full ? '100%' : 'calc(100% - 64px)',
          m: full ? 0 : 4,
          bgcolor: 'var(--bg-page)', color: 'var(--text-primary)',
          borderRadius: full ? 0 : 'var(--radius-lg)', display: 'flex', flexDirection: 'column', overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.25, px: { xs: 2, sm: 3 }, py: { xs: 1.25, sm: 1.75 },
          bgcolor: 'var(--surface)', borderBottom: '1px solid', borderColor: 'var(--divider)', flexShrink: 0,
        }}
      >
        <IconDot icon={isQuestion ? <QuizRounded sx={{ fontSize: 20 }} /> : <ToggleOnRounded sx={{ fontSize: 22 }} />} tone="primary" size={36} />
        <Typography id={titleId} variant="h3" component="h2" sx={{ flex: 1, minWidth: 0, color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>
          {c.title}
        </Typography>
        <Tooltip title="Fechar">
          <span>
            <IconButton aria-label="Fechar sem criar" onClick={() => a.onCloseCreator?.()} disabled={busy} sx={ICON_BTN_SX}>
              <CloseRounded />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', containerType: 'inline-size', containerName: 'criacao' }}>
        <Box
          sx={{
            p: { xs: 1.5, sm: 3 }, display: 'grid', gap: { xs: 1.5, sm: 2 },
            gridTemplateColumns: 'minmax(0, 1fr)',
            gridTemplateAreas: '"topo" "previa" "resto"',
            '@container criacao (min-width: 860px)': {
              gridTemplateColumns: 'minmax(0, 1.35fr) minmax(320px, 1fr)',
              gridTemplateAreas: '"topo previa" "resto previa"',
              gridTemplateRows: 'auto 1fr',
              columnGap: 3,
            },
          }}
        >
          <Box sx={{ gridArea: 'topo', display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 }, minWidth: 0 }}>
            {stepLabel}
            {stepType}
          </Box>
          <Box
            sx={{
              gridArea: 'previa', minWidth: 0,
              '@container criacao (min-width: 860px)': { position: 'sticky', top: 'var(--space-3)', alignSelf: 'start' },
            }}
          >
            <CreatorPreview c={c} compact={false} />
          </Box>
          <Box sx={{ gridArea: 'resto', display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 }, minWidth: 0 }}>
            {stepRules}
            {stepTarget}
            {stepVisibility}
            {stepFilters}
          </Box>
        </Box>
      </Box>

      <Box sx={{ flexShrink: 0, bgcolor: 'var(--surface)', borderTop: '1px solid', borderColor: 'var(--divider)', px: { xs: 2, sm: 3 }, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {c.errorMessage && <Hint tone="error" role="alert">{c.errorMessage}</Hint>}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flex: '1 1 300px', minWidth: 0 }}>
            <LockClockRounded aria-hidden="true" sx={{ fontSize: 20, color: 'var(--text-secondary)', mt: '1px', flexShrink: 0 }} />
            <Typography variant="body2" sx={{ color: 'var(--text-secondary)', lineHeight: 1.45 }}>{c.immutableNotice}</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
            <Button onClick={() => a.onCloseCreator?.()} disabled={busy} sx={{ ...QUIET_BTN_SX, flex: { xs: 1, sm: '0 0 auto' } }}>
              Cancelar
            </Button>
            <Button
              onClick={() => a.onSave?.()}
              disabled={!c.canSave || busy}
              disableElevation
              startIcon={busy ? <Spinner label="Criando" /> : <CheckRounded />}
              sx={{ ...PRIMARY_BTN_SX, flex: { xs: 2, sm: '0 0 auto' } }}
            >
              {c.saveLabel}
            </Button>
          </Box>
        </Box>
        <Box role="status" aria-live="polite" sx={SR_ONLY_SX}>{busy ? 'Criando…' : ''}</Box>
      </Box>
    </Dialog>
  );
}

/* ── View ─────────────────────────────────────────────────────────────── */
export default function CustomFieldsView({ vm, actions }) {
  const a = actions || {};
  const v = vm || {};

  // Primeiro desenho (no estúdio o mock já nasce com rename/confirmação abertos): nada rola nem rouba foco.
  const montou = useRef(false);
  const primeiroDesenho = !montou.current;
  useEffect(() => { montou.current = true; }, []);

  const cardRefs = useRef({});
  const pageTitleRef = useRef(null);
  const questionsTitleRef = useRef(null);

  // Apagou de verdade: a pergunta que estava em confirmação some da lista → o foco vai para o título da lista.
  const antes = useRef({ confirmId: v.confirmation?.id || null, ids: (v.questions || []).map((q) => q.id) });
  useEffect(() => {
    const prev = antes.current;
    const ids = (v.questions || []).map((q) => q.id);
    antes.current = { confirmId: v.confirmation?.id || null, ids };
    if (prev.confirmId && !v.confirmation && prev.ids.includes(prev.confirmId) && !ids.includes(prev.confirmId) && focoPerdido()) {
      levarFoco(questionsTitleRef.current || pageTitleRef.current, 'teclado');
    }
  }, [v.questions, v.confirmation]); // eslint-disable-line react-hooks/exhaustive-deps

  if (v.surface && v.surface !== 'screen') {
    return <Box sx={{ color: 'var(--text-primary)' }}>{v.block ? <BlockStage vm={v} actions={a} /> : null}</Box>;
  }

  const labels = v.labels || {};
  const st = v.viewState;
  const questions = v.questions || [];
  const planBlocked = !!v.planUsage && v.planUsage.canCreate === false;
  const newIcon = planBlocked ? <LockOutlined /> : <AddRounded />;
  const isEmpty = st === 'empty' || (st === 'ready' && questions.length === 0);
  const confirmando = st === 'ready' && !!v.confirmation;

  const goToAttention = () => {
    const q = questions.find((x) => (x.warnings || []).some((w) => w.status === 'warning' || w.status === 'error'));
    const el = q ? cardRefs.current[q.id] : null;
    if (!el) return;
    el.focus({ preventScroll: true });
    el.scrollIntoView({ block: 'center', behavior: semMovimento() ? 'auto' : 'smooth' });
  };

  const newButton = (
    <Button
      onClick={() => a.onNew?.()}
      startIcon={newIcon}
      disableElevation
      sx={{ ...PRIMARY_BTN_SX, width: { xs: '100%', sm: 'auto' } }}
    >
      {labels.actionLabel}
    </Button>
  );

  let body = null;
  if (st === 'loading') {
    body = <LoadingScreen />;
  } else if (st === 'error') {
    body = (
      <StateBlock
        icon={<ErrorRounded sx={{ fontSize: 32, color: 'var(--color-error)' }} />}
        title="Não foi possível carregar as perguntas"
        description={v.errorMessage}
        action={(
          <Button onClick={() => a.onRetry?.()} startIcon={<ReplayRounded />} sx={OUTLINE_BTN_SX}>
            Tentar novamente
          </Button>
        )}
      />
    );
  } else if (isEmpty) {
    body = (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <EmptyHero vm={v} a={a} newButtonIcon={newIcon} />
        {v.planUsage && <SummaryBar vm={{ ...v, summary: {} }} />}
        <Box
          sx={{
            display: 'grid', gap: 'var(--space-3)', gridTemplateColumns: 'minmax(0, 1fr)', alignItems: 'start',
            '@container perguntas (min-width: 900px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
          }}
        >
          <SuggestionsPanel vm={v} a={a} featured />
          <BasicDataPanel vm={v} a={a} />
        </Box>
      </Box>
    );
  } else {
    body = (
      <>
        <SummaryBar vm={v} onAttention={goToAttention} />
        <Box sx={LAYOUT_SX}>
          <QuestionList vm={v} a={a} quiet={primeiroDesenho} cardRefs={cardRefs} titleRef={questionsTitleRef} />
          <Box component="aside" aria-label="Dados básicos e perguntas prontas" sx={ASIDE_SX}>
            <BasicDataPanel vm={v} a={a} />
            <SuggestionsPanel vm={v} a={a} />
          </Box>
        </Box>
      </>
    );
  }

  return (
    <Box sx={{ containerType: 'inline-size', containerName: 'perguntas', color: 'var(--text-primary)', pb: confirmando ? FOLGA_AVISO : 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 'var(--space-3)' }}>
        <Box sx={{ minWidth: 0, flex: '1 1 320px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DynamicFormRounded sx={{ fontSize: 30, color: 'var(--color-primary-dark)' }} />
            <Typography ref={pageTitleRef} tabIndex={-1} variant="h1" component="h1" sx={{ ...FOCUS_TITLE_SX, color: 'var(--text-primary)', textWrap: 'pretty' }}>
              {labels.title}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mt: 0.5, maxWidth: 640 }}>{labels.subtitle}</Typography>
        </Box>
        {st !== 'error' && !isEmpty && newButton}
      </Box>

      {body}

      {v.creator && st !== 'error' && <CreatorDialog c={v.creator} a={a} />}
    </Box>
  );
}
